import { Icona } from '../../components/Icona';
import { VoceSpiegata } from '../../components/Spiegazione';
import { pernottamenti, spiegaCostoTotale, spiegaPrezzo, vociDelRiepilogo } from './riepilogo';
import { useUnitaDiMisura } from './unitaMisura';
import { stessiValoriCorreggibili } from './rigaCambiata';
import { Fragment, useEffect, useRef, useState } from 'react';
import { preventivi } from '../../api/preventivi';
import type {
  CalcoloTrasferta,
  CantierePreventivo,
  DettaglioPreventivo,
  EsitoSbloccoPrezzi,
  IncollaDalCarrello,
  ModificaRigaPreventivo,
  NuovaRigaPreventivo,
  RigaPreventivoLetta,
  SpesaDaScrivere,
  TestataPreventivo,
  TestiPreventivo,
  TrasfertaInOfferta,
  TrasfertaLetta,
  VersoSpostamento,
} from '../../api/preventivi';
import { commesse } from '../../api/commesse';
import { anagrafiche } from '../../api/acquisti';
import type { AnagraficaMago, CantiereMago } from '../../api/acquisti';
import { formattaData, formattaImporto, formattaNumero, formattaPercentuale } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';
import { TestiOfferta } from './TestiOfferta';
import { StampaOfferta } from '../stampa/StampaOfferta';
import { CicloPreventivo, BottoneControllo, BottoneInvio } from './CicloPreventivo';
import { StatoScrittura } from './StatoScrittura';
import { CercaArticolo } from './CercaArticolo';
import { ultimaDelBlocco } from './discendenti';
import { BottoneCarrello, FornitoreCarrello, PannelloCarrello } from './Carrello';
import { prezziDoppi, righeConPrezzoDoppio } from './prezziDoppi';
import { SezioneChiudibile } from './SezioneChiudibile';
import { TestoEsteso } from './TestoEsteso';

/*  La scheda del preventivo: l'unico documento dell'applicativo che stabilisce un prezzo.

    Tutto quello che si tocca qui rimbalza su tutto il resto — una quantità cambia le ore,
    le ore cambiano i giorni, i giorni cambiano i viaggi, i viaggi cambiano le trasferte,
    le trasferte cambiano i costi generali, e i costi generali cambiano il margine. Per
    questo ogni scrittura si aspetta indietro il preventivo intero dal server e lo
    sostituisce: ricalcolare quella catena qui vorrebbe dire una seconda copia delle
    formule del dominio, e due copie di una formula prima o poi dicono due numeri diversi.

    Il corpo è separato dal caricamento ed esportato, come per la commessa e per il
    rapportino: si disegna con dei dati in mano e si legge cosa esce, senza browser e senza
    database.  */

interface Props {
  preventivoId: string;
  onChiudi: () => void;
  onCambiato?: () => void;
  /*  Il documento aperto è diventato un altro: succede con la revisione, che è una copia
      e quindi ha un id nuovo. Senza, la scheda mostrerebbe la copia ma l'indirizzo e
      l'elenco resterebbero sul vecchio — e al primo F5 si tornerebbe sul documento che
      non si può più toccare, convinti di essere nella revisione.  */
  onSostituito?: (nuovoId: string) => void;
}

export function SchedaPreventivo({ preventivoId, onChiudi, onCambiato, onSostituito }: Props) {
  const [preventivo, setPreventivo] = useState<DettaglioPreventivo | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    let annullato = false;
    setPreventivo(null);
    setErrore(null);
    preventivi
      .leggi(preventivoId)
      .then((p) => !annullato && setPreventivo(p))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [preventivoId]);

  /*  Aprire un preventivo deve far vedere il preventivo.

      Misurato prima di correggerlo: cliccando una riga dell'elenco la scheda nasceva a
      1043 pixel dall'alto su una finestra alta 1031, cioe' dodici pixel SOTTO il bordo
      dello schermo. Zero pixel visibili. Chi cliccava vedeva la stessa schermata di prima
      con la riga evidenziata, e concludeva che il click fosse andato perso — e' il primo
      gesto di ogni sessione di lavoro, e mentiva.

      Si scorre una volta per preventivo aperto e non ad ogni ridisegno: la scheda si
      ridisegna ad ogni riga aggiunta, e riportare l'occhio in cima ogni volta sarebbe
      peggio del difetto che stiamo togliendo. Il ref ricorda per quale id lo si e' gia'
      fatto.

      L'elemento si cerca nel DOM invece di passare un ref dentro: l'<aside> appartiene al
      componente di sola presentazione, che le prove del frontend disegnano da solo con
      renderToStaticMarkup. Infilarci un ref vorrebbe dire cambiare la sua firma per una
      cosa che riguarda chi decide QUALE preventivo e' aperto, cioe' questo componente.  */
  const scorsoPer = useRef<string | null>(null);

  useEffect(() => {
    if (preventivo === null || scorsoPer.current === preventivoId) return;
    scorsoPer.current = preventivoId;
    document
      .querySelector('aside.scheda.preventivo')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [preventivoId, preventivo]);

  useEffect(() => {
    const chiudiConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', chiudiConEsc);
    return () => window.removeEventListener('keydown', chiudiConEsc);
  }, [onChiudi]);

  async function scrivi(azione: () => Promise<DettaglioPreventivo>) {
    setInCorso(true);
    setErrore(null);
    try {
      setPreventivo(await azione());
      onCambiato?.();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  if (errore && !preventivo) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!preventivo) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico il preventivo…</p>
      </aside>
    );
  }

  const id = preventivo.id;

  return (
    <CorpoPreventivo
      preventivo={preventivo}
      inCorso={inCorso}
      errore={errore}
      onChiudi={onChiudi}
      onAggiornato={(aggiornato) => {
        setPreventivo(aggiornato);
        onCambiato?.();
        if (aggiornato.id !== preventivoId) onSostituito?.(aggiornato.id);
      }}
      azioni={
        preventivo.modificabile
          ? {
              impostaTestata: (t) => void scrivi(() => preventivi.impostaTestata(id, t)),
              impostaCantiere: (c) => void scrivi(() => preventivi.impostaCantiere(id, c)),
              calcolaKm: (indirizzo) => preventivi.calcolaKm(id, indirizzo),
              aggiungiRiga: (r) => void scrivi(() => preventivi.aggiungiRiga(id, r)),
              modificaRiga: (rigaId, r) => void scrivi(() => preventivi.modificaRiga(id, rigaId, r)),
              duplicaRiga: (rigaId) => void scrivi(() => preventivi.duplicaRiga(id, rigaId)),
              spostaRiga: (rigaId, verso) =>
                void scrivi(() => preventivi.spostaRiga(id, rigaId, verso)),
              spostaRigaAlCodice: (rigaId, codice) =>
                void scrivi(() => preventivi.spostaRigaAlCodice(id, rigaId, codice)),
              rinumeraRighe: () => void scrivi(() => preventivi.rinumeraRighe(id)),
              rimuoviRiga: (rigaId) => void scrivi(() => preventivi.rimuoviRiga(id, rigaId)),
              sbloccaPrezzi: async () => {
                setInCorso(true);
                setErrore(null);
                try {
                  const esito = await preventivi.sbloccaPrezzi(id);
                  setPreventivo(esito.preventivo);
                  onCambiato?.();
                  return esito;
                } finally {
                  setInCorso(false);
                }
              },
              impostaTesti: (t) => scrivi(() => preventivi.impostaTesti(id, t)),
              aggiungiSpesa: (s) => void scrivi(() => preventivi.aggiungiSpesa(id, s)),
              modificaSpesa: (spesaId, s) => void scrivi(() => preventivi.modificaSpesa(id, spesaId, s)),
              dichiarazioni: (trasferta, costiGenerali) =>
                void scrivi(() => preventivi.dichiarazioni(id, trasferta, costiGenerali)),
              rimuoviSpesa: (spesaId) => void scrivi(() => preventivi.rimuoviSpesa(id, spesaId)),
              /*  Non passa da `scrivi`: l'errore — «ha dei sottocapitoli», «non e' un
                  capitolo» — va detto accanto al carrello, dove si e' scelto il posto, e
                  non in fondo alla scheda.  */
              incolla: async (i) => {
                setInCorso(true);
                setErrore(null);
                try {
                  setPreventivo(await preventivi.incolla(id, i));
                  onCambiato?.();
                } finally {
                  setInCorso(false);
                }
              },
            }
          : undefined
      }
    />
  );
}

/*  Le scritture possibili, dichiarate. Chi disegna la scheda non deve sapere come si
    chiamano le rotte, e passare `undefined` la rende una scheda in sola lettura — che è
    esattamente quello che serve su un preventivo già inviato, e quello che serve ai test.  */
export interface AzioniPreventivo {
  impostaTestata: (testata: TestataPreventivo) => void;
  impostaCantiere: (cantiere: CantierePreventivo) => void;
  /** L'indirizzo su cui calcolare. Omesso: quello salvato. */
  calcolaKm: (indirizzoCantiere?: string | null) => Promise<CalcoloTrasferta>;
  aggiungiRiga: (riga: NuovaRigaPreventivo) => void;
  modificaRiga: (rigaId: string, riga: ModificaRigaPreventivo) => void;
  duplicaRiga: (rigaId: string) => void;
  /*  Su, Giu, Dentro, Fuori. La scheda non decide se la mossa è possibile: lo sa il
      dominio, che conosce le sorelle e i livelli, e risponde con un motivo leggibile
      quando non lo è. Duplicare quel giudizio qui vorrebbe dire due regole che divergono,
      e la schermata che spegne un bottone che avrebbe funzionato.  */
  spostaRiga: (rigaId: string, verso: VersoSpostamento) => void;
  /*  Spostare scrivendo il numero invece di premere la freccia: le frecce restano il
      gesto piu' breve per scavalcare la riga accanto, ma per mandare una voce dal fondo
      in testa vogliono ottanta clic.  */
  spostaRigaAlCodice: (rigaId: string, codice: number) => void;
  rinumeraRighe: () => void;
  rimuoviRiga: (rigaId: string) => void;
  /*  Risponde invece di limitarsi a scrivere: chi sblocca deve vedere quali prezzi si
      sono mossi, non solo un totale nuovo di cui fidarsi sulla parola.  */
  sbloccaPrezzi: () => Promise<EsitoSbloccoPrezzi>;
  /*  Risponde con una promessa perche' il pannello dei testi mostra l'esito accanto al
      bottone: e' l'unico punto della scheda in cui si scrive un testo lungo, e perdere
      la nota per un messaggio comparso in fondo alla pagina sarebbe seccante.  */
  impostaTesti: (testi: TestiPreventivo) => Promise<unknown>;
  aggiungiSpesa: (spesa: SpesaDaScrivere) => void;
  modificaSpesa: (spesaId: string, spesa: SpesaDaScrivere) => void;
  /** Dove si leggono trasferta e costi generali in offerta. Non tocca il Totale. */
  dichiarazioni: (trasferta: TrasfertaInOfferta, costiGenerali: boolean) => void;
  rimuoviSpesa: (spesaId: string) => void;
  /** Incolla il carrello personale dentro un capitolo (15/10/2026). */
  incolla?: (incolla: IncollaDalCarrello) => Promise<unknown>;
}

export function CorpoPreventivo({
  preventivo: p,
  inCorso,
  errore,
  onChiudi,
  azioni,
  onAggiornato,
}: {
  preventivo: DettaglioPreventivo;
  inCorso: boolean;
  errore?: string | null;
  onChiudi: () => void;
  azioni?: AzioniPreventivo;
  /*  Il preventivo dopo una transizione di stato. Separato da `azioni` perche' quelle
      cambiano il contenuto del documento e questo ne cambia il ciclo: mandare un'offerta
      si puo' anche quando il documento non e' piu' modificabile, ed e' anzi da li' che
      partono conferma e revisione.  */
  onAggiornato?: (aggiornato: DettaglioPreventivo) => void;
}) {
  return (
    <aside
      className="scheda preventivo"
      aria-label={`Preventivo ${p.numeroCompleto ?? 'senza numero'}`}
    >
      <header>
        <div>
          <p className="chiave">
            {/*  Il numero completo, con la revisione dentro: e' quello che il cliente ha
                 sul PDF, ed e' quello che cita al telefono.  */}
            <strong>{p.numeroCompleto ?? 'senza numero'}</strong>
            {p.revisione > 0 && <span className="stato">revisione {p.revisione}</span>}
            <span className={`stato stato-${p.stato.toLowerCase()}`}>{p.stato}</span>
          </p>
          <h2>{p.oggettoPerCliente ?? p.descrizioneInterna}</h2>
          {/*  IL CLIENTE IN GRANDE, 15/10/2026: «mi sarebbe piaciuto che in alto rimanesse
               l'anagrafica del cliente». L'anagrafica intera no — si compila da sola da
               Mago — ma il nome si', perche' e' la prima cosa per cui si riconosce
               un'offerta fra venti aperte nella stessa mattina.  */}
          <p className="cliente-testata">{p.cliente ?? p.codiceClienteMago}</p>
          <p className="riferimenti">
            {[
              p.dataValidita && `valido fino al ${formattaData(p.dataValidita)}`,
              p.tecnicoResponsabile,
              p.numeroOrdineCliente && `ordine ${p.numeroOrdineCliente}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {!p.modificabile && (
        <p className="nota">
          {p.stato === 'Inviato'
            ? 'Documento uscito: per cambiarlo si apre una revisione, così resta traccia di quello che il cliente ha ricevuto.'
            : 'Preventivo chiuso: si legge e basta.'}
        </p>
      )}

      {onAggiornato !== undefined && (
        <CicloPreventivo preventivo={p} onCambiato={onAggiornato} />
      )}

      {/*  L'ORDINE DELLA SCHEDA E' UNA SCALETTA, e non e' come stava prima.

           Chiesto in riunione il 14/09 — «spostare in fondo le spese di cantiere, le
           trasferte e gli oneri accessori, subito dopo l'elenco dei materiali» — e
           completato il 15/09 con il riepilogo dei costi.

           Il motivo e' che quasi tutto quello che sta sotto DIPENDE dal materiale. Le
           trasferte si calcolano dalle ore del preventivo e dai chilometri; le spese
           accessorie in percentuale si calcolano sul costo diretto; il riepilogo somma
           tutto. Chiederle prima vuol dire chiedere di decidere su numeri che non
           esistono ancora, e poi tornare indietro a rifarle — che e' esattamente quello
           che succedeva.

           Restano in alto solo le due cose che si guardano PER SAPERE COM'E' ANDATA: il
           margine e la barra del prezzo. Sono la risposta alla domanda per cui si apre un
           preventivo gia' fatto, e in fondo alla pagina vorrebbe dire scorrere ogni volta.
           Il riepilogo dei costi invece e' un'altra cosa: e' il dettaglio, si legge dopo
           aver messo dentro tutto, ed e' l'ultima verifica prima di stampare.  */}
      <Verdetto conti={p.conti} />

      <BarraPrezzo conti={p.conti} />

      <Parametri preventivo={p} inCorso={inCorso} azioni={azioni} />

      <Righe preventivo={p} inCorso={inCorso} azioni={azioni} />

      <CantiereESquadra preventivo={p} inCorso={inCorso} azioni={azioni} />

      <SpeseAccessorie preventivo={p} inCorso={inCorso} azioni={azioni} />

      <Riepilogo preventivo={p} />

      {/*  LO STESSO VERDETTO UNA SECONDA VOLTA, in fondo al riepilogo dei costi.

           «Arrivi in fondo, hai il tuo riepilogo e vedi quali sono i margini» (Andrea,
           01/10/2026). Chi COMPILA scende: mette le righe, il cantiere, le spese, e la
           domanda «quanto ci guadagno» gli viene in mente qui, dove il numero e' appena
           finito di fare. Risalire in cima per leggerlo vuol dire perdere il punto in cui
           si era.

           E in cima resta lo stesso: chi RIAPRE un'offerta gia' fatta non sta compilando
           niente, apre per sapere com'e' andata, e scorrere fino in fondo per saperlo
           sarebbe la stessa seccatura al contrario. Sono due momenti diversi della stessa
           scheda, e lo stesso numero scritto due volte costa una riga.  */}
      <Verdetto conti={p.conti} dove="fondo" />

      {/*  E LA BARRA, sotto il verdetto (15/10/2026): «se stai costruendo un preventivo
           grande difficilmente torni in cima per vedere com'e' la ripartizione dei costi».
           Fino al 04/10 si era deciso di lasciarla solo in cima; chi compila ha chiesto il
           contrario, e ha ragione per la stessa ragione del verdetto qui sopra.  */}
      <BarraPrezzo conti={p.conti} dove="fondo" />

      {/*  In fondo perche' e' l'ultima cosa che si guarda: prima si costruisce il prezzo,
           poi si decide cosa scriverci intorno. Le clausole si spuntano qui e si
           riscrivono altrove — chi non ha quel permesso non deve nemmeno vedere il
           bottone per provarci.  */}
      <TestiOfferta
        preventivo={p}
        modificabile={azioni !== undefined}
        onSalva={(testi) => azioni?.impostaTesti(testi) ?? Promise.resolve()}
      />

      {/*  Ultima in fondo, dopo i testi: si stampa quando tutto il resto è a posto. Non
           dipende da `azioni` — un preventivo gia' inviato si ristampa eccome, ed e'
           anzi il caso in cui serve piu' spesso.

           Qui dentro, di fianco a «scarica il Word», sta anche il bottone che MANDA
           l'offerta: chiesto il 15/09, e per il motivo che si legge in BottoneInvio —
           scaricare e mandare sono un gesto solo.  */}
      <StampaOfferta
        preventivoId={p.id}
        /*  I codici a prezzi doppi bloccano la stampa (punto 4.8 del 01/10). Si
            ricalcolano qui e non arrivano dalla tabella delle righe: sono due
            componenti diversi, e passarseli vorrebbe dire far sapere alla tabella che
            esiste una stampa.  */
        prezziDoppi={prezziDoppi(p.righe).map((d) => d.codice)}
        invio={
          onAggiornato !== undefined ? (come) => (
            <>
              {/*  Il controllo PRIMA dell'invio, perche' e' l'ordine in cui vanno fatti
                   e perche' letti dall'alto i due bottoni raccontano il gesto: l'ho
                   riletta, la mando.  */}
              <BottoneControllo preventivo={p} onCambiato={onAggiornato} />
              <BottoneInvio preventivo={p} onCambiato={onAggiornato} come={come} />
            </>
          ) : undefined
        }
      />

      {errore && <p className="errore">{errore}</p>}
    </aside>
  );
}

/*  Il margine in cima, perché è la domanda che porta ad aprire un preventivo.

    In percentuale sul PREZZO e non sul costo: «ci guadagno il venti per cento» detto da
    chi vende vuol dire venti su cento incassati. Sul costo lo stesso preventivo direbbe
    venticinque, e su una commessa grossa la differenza sono soldi veri.  */
function Verdetto({
  conti: c,
  dove = 'alto',
}: {
  conti: DettaglioPreventivo['conti'];
  /*  Lo stesso verdetto esce due volte, e il posto cambia solo lo spazio che si prende:
      in cima apre la scheda, in fondo chiude il riepilogo e vuole dell'aria sopra.  */
  dove?: 'alto' | 'fondo';
}) {
  if (c.prezzo <= 0)
    return (
      <p className={`verdetto assente${dove === 'fondo' ? ' in-fondo' : ''}`}>
        Nessuna riga valorizzata: senza righe non c'è né un prezzo né un margine.
      </p>
    );

  const negativo = c.margine < 0;

  return (
    <p className={`verdetto${negativo ? ' allarme' : ''}${dove === 'fondo' ? ' in-fondo' : ''}`}>
      Prezzo <strong>{formattaImporto(c.prezzo)}</strong>, margine{' '}
      <strong>{formattaImporto(c.margine)}</strong>
      {c.marginePercentuale !== null && <> ({formattaPercentuale(c.marginePercentuale)})</>} su{' '}
      {formattaImporto(c.costoTotale)} di costo.{' '}
      {negativo
        ? 'Così com’è, questo lavoro si fa rimettendoci.'
        : 'Il margine è sul prezzo, non sul costo.'}
    </p>
  );
}

/*  Il prezzo smontato in una barra sola.

    Serve a far vedere quello che una riga di testo non fa vedere: non quanto vale il
    margine, ma quanto è grosso RISPETTO al resto. «14,12%» è un numero che si legge;
    una fetta lunga un settimo della barra si vede, e si vede da lontano — che è la
    differenza fra accorgersi di un preventivo tirato e doverlo calcolare.

    È **la stessa barra della commessa**: stessa struttura, stessi colori, stessa
    regola sulla scala — di norma il prezzo, e quando i costi lo superano diventa il
    costo, con una tacca dove stava il prezzo. Le due schermate rispondono alla stessa
    domanda, «quanto di quello che incasso mi resta», e due risposte disegnate in due
    modi diversi si imparano a leggere due volte.

    I pezzi sono le voci del riepilogo qui sotto, nello stesso ordine, e insieme fanno
    esattamente il costo totale: un pezzo che mancasse lascerebbe un buco nella traccia
    e gonfierebbe il margine di quello che il buco vale. Gli opzionali restano fuori,
    come nel riepilogo — non sono ancora venduti.

    Nella legenda ci sono i nomi e non gli importi: gli importi stanno nella tabella
    subito sotto, uno per riga e allineati. La barra serve alle proporzioni, la tabella
    ai numeri, e ripeterli qui vorrebbe dire leggere due volte la stessa colonna.  */
function BarraPrezzo({
  conti: c,
  dove = 'cima',
}: {
  conti: DettaglioPreventivo['conti'];
  /*  In fondo e' la copia per chi compila (15/10/2026): un filo piu' alta, ed e' li'
      che la si guarda con calma, sotto i totali.  */
  dove?: 'cima' | 'fondo';
}) {
  if (c.prezzo <= 0) return null;

  const base = Math.max(c.prezzo, c.costoTotale);
  const quota = (valore: number) => `${(valore / base) * 100}%`;

  const pezzi = [
    { classe: 'materiali', nome: 'materiali', valore: c.costoMateriali },
    /*  I subappalti subito dopo i materiali, perché è da lì che sono usciti: si comprano
        come si compra un quadro elettrico. La fetta compare solo quando ce n'è uno, così
        su un preventivo normale la barra resta quella di prima.  */
    { classe: 'subappalti', nome: 'subappalti', valore: c.costoSubappalti },
    { classe: 'manodopera', nome: 'manodopera', valore: c.costoManodopera },
    { classe: 'accessorie', nome: 'spese accessorie', valore: c.speseAccessorie },
    { classe: 'generali', nome: 'costi generali', valore: c.costiGenerali },
  ].filter((p) => p.valore > 0);

  const margine = Math.max(0, c.margine);
  const inPerdita = c.margine < 0;

  return (
    <div className={`barra-costi barra-prezzo${dove === 'fondo' ? ' in-fondo' : ''}`}>
      <div
        className="traccia"
        role="img"
        aria-label={`Su ${formattaImporto(c.prezzo)} di prezzo: ${formattaImporto(
          c.costoTotale,
        )} di costo e ${formattaImporto(c.margine)} di margine.`}
      >
        {pezzi.map((p) => (
          <span key={p.classe} className={p.classe} style={{ width: quota(p.valore) }} />
        ))}
        {margine > 0 && <span className="margine" style={{ width: quota(margine) }} />}

        {/*  Col margine negativo la barra è piena di costo e il prezzo cade dentro:
             la tacca è l'unico posto in cui si vede di quanto si sfora.  */}
        {inPerdita && (
          <span className="tacca oltre" style={{ left: quota(c.prezzo) }}>
            <em>prezzo</em>
          </span>
        )}
      </div>

      {/*  LA LEGENDA DICE ANCHE QUANTO, in percentuale sul prezzo (15/10/2026): «scritto
           cosi' in piccolo che non lo vedevo... se rendessi questi grafici nominativi con
           la descrizione molto piu' grossa». Le percentuali e non gli importi: gli
           importi stanno nel riepilogo, incolonnati, e la barra serve alle proporzioni —
           la percentuale e' proprio la proporzione scritta.  */}
      <p className="legenda">
        {pezzi.map((p) => (
          <span key={p.classe}>
            <i className={p.classe} aria-hidden="true" />
            {p.nome} <strong>{percentualeDelPrezzo(p.valore, c.prezzo)}</strong>
          </span>
        ))}
        {margine > 0 && (
          <span>
            <i className="margine" aria-hidden="true" />
            margine <strong>{percentualeDelPrezzo(margine, c.prezzo)}</strong>
          </span>
        )}
      </p>
    </div>
  );
}

/*  Quanto pesa una voce sul prezzo, arrotondato all'intero: e' una legenda, non un
    conto, e «46,3 %» si legge peggio di «46 %» senza dire niente di piu'.  */
function percentualeDelPrezzo(valore: number, prezzo: number): string {
  return prezzo > 0 ? `${Math.round((valore / prezzo) * 100)}%` : '';
}

/*  Dove finiscono i soldi, nell'ordine in cui un impiantista fa i conti sul foglio:
    materiali e manodopera fanno il costo diretto, le accessorie ci si aggiungono, i costi
    generali pesano su tutti e due, e il ricarico fa il prezzo.

    Le accessorie sono aperte una per una e non riassunte in un totale: sono la voce che
    su un preventivo sbagliato manca del tutto, e nasconderla dietro una somma vuol dire
    non accorgersi che è a zero.  */
function Riepilogo({ preventivo: p }: { preventivo: DettaglioPreventivo }) {
  const c = p.conti;

  /*  LE VOCI VENGONO DA `riepilogo.ts` e non sono più scritte qui.

      Le si è spostate il 20/09/2026 mettendo i popup, e non era una ripulitura: erano
      scritte a mano una per una dentro il JSX, e **mancava la riga dei subappalti**. Su
      un preventivo con 2.400 € di subappalto le voci a schermo sommavano 2.400 € meno
      del «Costo totale» scritto sotto, perché i subappalti stanno nel totale e non
      avevano una riga. Con l'elenco in una funzione a parte c'è una prova che somma le
      voci e le confronta con il totale, e un buco così non passa più.  */
  const voci = vociDelRiepilogo(p);

  /*  Il titolo c'e' da quando il riepilogo sta in fondo: prima veniva subito dopo la barra
      del prezzo e si capiva da solo, adesso arriva dopo tre sezioni che ce l'hanno e senza
      sembrerebbe la coda dell'ultima invece di una cosa a se'.  */
  return (
    <SezioneChiudibile classe="riepilogo-costi" titolo="Riepilogo dei costi">
      <p className="nota nota-spiegazioni">
        Ogni voce si apre: dentro c'è il conto da cui esce, numero per numero.
      </p>
    <table className="consuntivo riepilogo-preventivo">
      <thead>
        <tr>
          <th>Voce</th>
          <th className="destra">Costo</th>
        </tr>
      </thead>
      <tbody>
        {voci.map((voce) => (
          <tr key={voce.chiave}>
            <td>
              <VoceSpiegata nome={voce.nome} righe={voce.spiegazione}>
                {voce.dettaglio !== undefined && (
                  <span className="dettaglio">{voce.dettaglio}</span>
                )}
              </VoceSpiegata>
            </td>
            <td className="destra">{formattaImporto(voce.importo)}</td>
          </tr>
        ))}

        {/*  «Nessuna spesa accessoria» non è una riga che manca, è una cosa da sapere: è
             la voce che su un preventivo sbagliato manca del tutto, e riassumerla in un
             totale vorrebbe dire non accorgersi che è a zero.  */}
        {c.accessorie.length === 0 && (
          <tr className="fuori">
            <td>
              <span className="nome">Spese accessorie</span>
              <span className="dettaglio">
                <em>
                  {c.supplementoOrario > 0
                    ? 'né sicurezza né progettazione: la trasferta è già nell’ora qui sopra'
                    : 'nessuna: né trasferte, né sicurezza, né progettazione'}
                </em>
              </span>
            </td>
            <td className="destra">—</td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <td>
            <VoceSpiegata nome="Costo totale" righe={spiegaCostoTotale(p)} />
          </td>
          <td className="destra">{formattaImporto(c.costoTotale)}</td>
        </tr>

        {/*  Il «di cui», sotto e non dentro: e' gia' compreso nel prezzo e va solo
             dichiarato — «di cui oneri della sicurezza X €, non soggetti a ribasso».
             Sommarlo qui vorrebbe dire contarlo due volte, e lo scoprirebbe il cliente
             confrontando il totale con la riga che gli sta sotto.  */}
        {c.diCuiEsposto > 0 && (
          <tr className="fuori">
            <td>
              di cui{' '}
              {c.accessorie
                .filter((v) => !v.nelTotale)
                .map((v) => v.nome.toLowerCase())
                .join(', ')}
              <span className="dettaglio">già compreso nel prezzo: si dichiara, non si somma</span>
            </td>
            <td className="destra">{formattaImporto(c.diCuiEsposto)}</td>
          </tr>
        )}
        {/*  Con uno sconto globale il prezzo si legge in tre righe e non in una: prima
             quello di listino, poi lo sconto, poi quello che il cliente paga. Nasconderlo
             dentro un totale unico è il modo di non sapere più a che condizioni si è
             venduto.  */}
        {c.aggiustamento !== 0 && (
          <>
            <tr>
              <td>
                Prezzo di listino
                <span className="dettaglio">
                  ricarico {formattaPercentuale(p.ricaricoGeneralePercentuale)}, dove non è
                  dichiarato riga per riga
                </span>
              </td>
              <td className="destra">{formattaImporto(c.prezzoPieno)}</td>
            </tr>
            <tr>
              <td>
                {c.aggiustamento < 0 ? 'Sconto' : 'Aumento'} globale
                <span className="dettaglio">
                  {formattaPercentuale(p.aggiustamentoPercentuale)} sul totale: i prezzi
                  unitari restano quelli di sopra
                </span>
              </td>
              <td className="destra">{formattaImporto(c.aggiustamento)}</td>
            </tr>
          </>
        )}
        <tr>
          <td>
            <VoceSpiegata nome="Prezzo" righe={spiegaPrezzo(p)}>
              {c.aggiustamento === 0 && (
                <span className="dettaglio">
                  ricarico {formattaPercentuale(p.ricaricoGeneralePercentuale)}, dove non è
                  dichiarato riga per riga
                </span>
              )}
            </VoceSpiegata>
          </td>
          <td className="destra">{formattaImporto(c.prezzo)}</td>
        </tr>
        {c.prezzoOpzionali > 0 && (
          <tr className="fuori">
            <td>
              Opzionali, fuori dal totale
              <span className="dettaglio">se il cliente le prende, si aggiungono a quello sopra</span>
            </td>
            <td className="destra">{formattaImporto(c.prezzoOpzionali)}</td>
          </tr>
        )}
      </tfoot>
    </table>
    </SezioneChiudibile>
  );
}

/*  I numeri con cui si fa il prezzo, e le date del documento.

    Stanno in un blocco che si apre e non sempre a schermo: si toccano una volta per
    preventivo — spesso mai, perché i valori buoni sono quelli di sempre — mentre righe e
    trasferte si toccano continuamente. Tenerli aperti vorrebbe dire mettere sette campi
    che quasi nessuno guarda fra l'intestazione e le righe, che sono la ragione per cui si
    apre un preventivo.

    Il ricarico generale è quello che fa il prezzo dove la riga non ne dichiara uno suo.
    A zero il preventivo vende a costo, ed è la ragione per cui il blocco si apre da solo
    finché resta a zero: non è un valore predefinito ragionevole, è un valore da mettere.  */
function Parametri({
  preventivo: p,
  inCorso,
  azioni,
}: {
  preventivo: DettaglioPreventivo;
  inCorso: boolean;
  azioni?: AzioniPreventivo;
}) {
  /*  APERTO DI PARTENZA dal 15/10/2026: si apriva solo col ricarico a zero, e le date
      del preventivo — validita' compresa — restavano dietro un titolo che nessuno
      cliccava («quando apri il preventivo non e' aperto questo»). Chi vuole lo chiude.  */
  const [parametriAperti, setParametriAperti] = useState(true);
  const [oggetto, setOggetto] = useState(p.oggettoPerCliente ?? '');
  const [descrizione, setDescrizione] = useState(p.descrizioneInterna);
  const [dataPreventivo, setDataPreventivo] = useState(p.dataPreventivo ?? '');
  const [validita, setValidita] = useState(p.dataValidita ?? '');
  const [tariffa, setTariffa] = useState(String(p.tariffaManodoperaOraria));
  const [generali, setGenerali] = useState(String(p.costiGeneraliPercentuale));
  const [ricarico, setRicarico] = useState(String(p.ricaricoGeneralePercentuale));
  const [suOre, setSuOre] = useState(p.ricaricaManodopera);
  const [aggiustamento, setAggiustamento] = useState(String(p.aggiustamentoPercentuale));

  useEffect(() => {
    setOggetto(p.oggettoPerCliente ?? '');
    setDescrizione(p.descrizioneInterna);
    setDataPreventivo(p.dataPreventivo ?? '');
    setValidita(p.dataValidita ?? '');
    setTariffa(String(p.tariffaManodoperaOraria));
    setGenerali(String(p.costiGeneraliPercentuale));
    setRicarico(String(p.ricaricoGeneralePercentuale));
    setSuOre(p.ricaricaManodopera);
    setAggiustamento(String(p.aggiustamentoPercentuale));
  }, [p]);

  if (!azioni) {
    return (
      <p className="nota">
        Manodopera a {formattaImporto(p.tariffaManodoperaOraria)} l'ora, costi generali{' '}
        {formattaPercentuale(p.costiGeneraliPercentuale)}, ricarico{' '}
        {formattaPercentuale(p.ricaricoGeneralePercentuale)}
        {p.ricaricaManodopera ? ' su materiale e ore' : ' sul solo materiale'}
        {p.aggiustamentoPercentuale !== 0 && (
          <>
            , {p.aggiustamentoPercentuale < 0 ? 'sconto' : 'aumento'} globale{' '}
            {formattaPercentuale(p.aggiustamentoPercentuale)}
          </>
        )}
        .
      </p>
    );
  }

  function daiCampi(): TestataPreventivo {
    return {
      descrizioneInterna: descrizione.trim() === '' ? p.descrizioneInterna : descrizione.trim(),
      oggettoPerCliente: oggetto.trim() === '' ? null : oggetto.trim(),
      dataPreventivo: dataPreventivo === '' ? null : dataPreventivo,
      dataValidita: validita === '' ? null : validita,
      dataPrevistaInizioLavori: p.dataPrevistaInizioLavori,
      dataPrevistaFineLavori: p.dataPrevistaFineLavori,
      tariffaManodoperaOraria: numeroDecimale(tariffa) ?? p.tariffaManodoperaOraria,
      costiGeneraliPercentuale: numeroDecimale(generali) ?? 0,
      ricaricoGeneralePercentuale: numeroDecimale(ricarico) ?? 0,
      tecnicoResponsabileId: p.tecnicoResponsabileId,
      ricaricaManodopera: suOre,
      aggiustamentoPercentuale: numeroDecimale(aggiustamento) ?? 0,
    };
  }

  /*  La stessa testata letta dal documento salvato. Accanto a `daiCampi` apposta, come
      nel cantiere: sono due letture della stessa scheda, e tenerle lontane vorrebbe dire
      che fra sei mesi una ha un campo in piu' dell'altra e il salvataggio automatico
      smette di accorgersi di quel campo.  */
  function comeSalvato(): TestataPreventivo {
    return {
      descrizioneInterna: p.descrizioneInterna,
      oggettoPerCliente: p.oggettoPerCliente ?? null,
      dataPreventivo: p.dataPreventivo ?? null,
      dataValidita: p.dataValidita ?? null,
      dataPrevistaInizioLavori: p.dataPrevistaInizioLavori,
      dataPrevistaFineLavori: p.dataPrevistaFineLavori,
      tariffaManodoperaOraria: p.tariffaManodoperaOraria,
      costiGeneraliPercentuale: p.costiGeneraliPercentuale,
      ricaricoGeneralePercentuale: p.ricaricoGeneralePercentuale,
      tecnicoResponsabileId: p.tecnicoResponsabileId,
      ricaricaManodopera: p.ricaricaManodopera,
      aggiustamentoPercentuale: p.aggiustamentoPercentuale,
    };
  }

  const allineato = JSON.stringify(daiCampi()) === JSON.stringify(comeSalvato());

  /*  `modifiche` perche' una spunta si salva nel momento in cui si spunta, e in quel
      momento lo stato di React non e' ancora cambiato: il valore nuovo arriva da qui, e
      non si legge una casella che sta per diventare un'altra cosa.  */
  function salva(modifiche: Partial<TestataPreventivo> = {}) {
    azioni!.impostaTestata({ ...daiCampi(), ...modifiche });
  }

  function salvaSeCambiato() {
    if (!azioni || inCorso || allineato) return;

    salva();
  }

  return (
    <details
      className="provenienza parametri"
      open={parametriAperti}
      onToggle={(e) => setParametriAperti(e.currentTarget.open)}
    >
      <summary>Parametri e date</summary>

      {p.ricaricoGeneralePercentuale === 0 && (
        <p className="nota">
          Il ricarico è a zero: così com'è, questo preventivo vende a costo.
        </p>
      )}

      {/*  DUE FILE, e non otto campi in coda uno all'altro.

           I primi due sono frasi — il titolo che legge il cliente e la descrizione con
           cui lo riconosciamo noi — e in una fila sola restavano larghi come un campo
           data: si vedeva mezzo titolo e il resto scorreva dentro. Gli altri sei sono
           numeri e date, larghi quanto basta per quello che contengono.

           E' anche una divisione di significato: sopra COSA E' questo preventivo, sotto
           CON CHE NUMERI si calcola.  */}
      {/*  L'ascolto sta sul gruppo e non su ogni casella: `onBlur` in React e'
           `focusout`, che risale, quindi una riga sola copre tutte le caselle di qui
           dentro — e soprattutto copre anche quella che qualcuno aggiungera' fra un mese
           senza sapere che c'era un salvataggio da agganciare.  */}
      <div className="campi" onBlur={salvaSeCambiato}>
        <div className="fila testi">
          <label>
            <span>Oggetto per il cliente</span>
            <input
              className="campo"
              value={oggetto}
              placeholder="Il titolo che legge il cliente in offerta"
              onChange={(e) => setOggetto(e.target.value)}
            />
          </label>
          <label>
            <span>Descrizione interna</span>
            <input className="campo" value={descrizione} onChange={(e) => setDescrizione(e.target.value)} />
          </label>
        </div>

        <div className="fila numeri">
          <label className="stretto">
            <span>Data</span>
            <input
              className="campo"
              type="date"
              value={dataPreventivo}
              onChange={(e) => setDataPreventivo(e.target.value)}
            />
          </label>
          <label className="stretto">
            <span>Valido fino al</span>
            <input
              className="campo"
              type="date"
              value={validita}
              onChange={(e) => setValidita(e.target.value)}
            />
          </label>
          <label className="stretto">
            <span>Manodopera €/ora</span>
            <input
              className="campo"
              inputMode="decimal"
              value={tariffa}
              onChange={(e) => setTariffa(e.target.value)}
            />
          </label>
          <label className="stretto">
            <span>Costi generali %</span>
            <input
              className="campo"
              inputMode="decimal"
              value={generali}
              onChange={(e) => setGenerali(e.target.value)}
            />
          </label>
          <label className="stretto">
            <span>Ricarico %</span>
            <input
              className="campo"
              inputMode="decimal"
              value={ricarico}
              onChange={(e) => setRicarico(e.target.value)}
            />
          </label>

          {/*  Il «ricalcolo globale» chiesto in riunione: «aggiungi il 5%, fai lo sconto
               del 10% di tutto». Col segno, perché sono la stessa cosa in due direzioni,
               e in fondo al conto: i prezzi unitari restano quelli di listino — «poi se
               vi chiedono i prezzi unitari non torna un cacchio».  */}
          <label className="stretto">
            <span>Sconto o aumento %</span>
            <input
              className="campo"
              inputMode="decimal"
              placeholder="0"
              value={aggiustamento}
              onChange={(e) => setAggiustamento(e.target.value)}
            />
          </label>
        </div>

        {/*  La tariffa dei preventivi è unica e già netta — deciso in riunione — quindi
             di norma il ricarico tocca il solo materiale. La spunta c'è per chi
             preventiva la manodopera a costo e ci mette sopra il margine.  */}
        {/*  La spunta si salva nel momento in cui si spunta e non uscendo: e' una
             decisione sola, presa e finita, e aspettare il fuoco che se ne va vorrebbe
             dire vedere i numeri sotto restare quelli di prima per un gesto intero.  */}
        <label className="spunta">
          <input
            type="checkbox"
            checked={suOre}
            onChange={(e) => {
              setSuOre(e.target.checked);
              salva({ ricaricaManodopera: e.target.checked });
            }}
          />
          <span>
            Ricarica anche le ore — di norma no: i {formattaImporto(p.tariffaManodoperaOraria)}{' '}
            l'ora sono già il prezzo della manodopera
          </span>
        </label>

        <StatoScrittura inCorso={inCorso} allineato={allineato} />
      </div>
    </details>
  );
}

/*  CHI FA IL SUBAPPALTO.

    Si cerca fra i fornitori di Mago e non si scrive a mano, per la stessa ragione per cui
    il cliente si cerca: un nome battuto a mano e' un nome che nessuno ritrova, e fra sei
    mesi «Ediltecnica» e «EDIL TECNICA SRL» sono due imprese diverse per chiunque guardi.

    Si salva da sola, senza passare dal «Salva la riga»: e' una scelta sola, si prende con
    un gesto, ed e' la stessa regola della distinta qui accanto.

    Il nome si conserva sulla riga insieme al codice, come una fotografia: serve a
    rileggere chi era senza andarlo a ripescare in Mago, ed e' quello che si stampa se un
    domani il subappalto va in offerta.  */
function ChiLoFa({
  riga,
  inCorso,
  azioni,
}: {
  riga: RigaPreventivoLetta;
  inCorso: boolean;
  azioni: AzioniPreventivo;
}) {
  const [frase, setFrase] = useState('');
  const [trovati, setTrovati] = useState<AnagraficaMago[]>([]);

  useEffect(() => {
    const cercata = frase.trim();
    if (cercata.length < 2) {
      setTrovati([]);
      return;
    }

    //  Mezzo secondo, come nelle altre ricerche: le anagrafiche stanno su un altro
    //  database e una query per lettera battuta sono dieci query per un nome.
    let annullato = false;
    const attesa = setTimeout(() => {
      anagrafiche
        .fornitori(cercata)
        .then((r) => !annullato && setTrovati(r.slice(0, 6)))
        .catch(() => undefined);
    }, 500);

    return () => {
      annullato = true;
      clearTimeout(attesa);
    };
  }, [frase]);

  const scegli = (f: AnagraficaMago | null) => {
    setFrase('');
    setTrovati([]);
    azioni.modificaRiga(riga.id, {
      descrizione: riga.descrizione,
      quantita: riga.quantita,
      oreUnitarie: riga.oreUnitarie,
      posaACorpo: riga.posaACorpo,
      subappalto: true,
      codiceFornitoreMago: f?.codiceMago ?? null,
      fornitore: f?.denominazione ?? null,
      costoUnitarioMateriale: riga.costoUnitarioMateriale,
      prezzoListinoUnitario: riga.prezzoListinoUnitario,
      unitaMisura: riga.unitaMisura,
      siglaProduttore: riga.siglaProduttore,
      codiceArticoloProduttore: riga.codiceArticoloProduttore,
      opzionale: riga.opzionale,
      visibileInStampa: riga.visibileInStampa,
      prezzoBloccato: riga.prezzoBloccato,
      ricaricoSpecificoPercentuale: riga.ricaricoSpecificoPercentuale,
      giaComprensivoDiMargine: riga.giaComprensivoDiMargine,
    });
  };

  if (riga.fornitore)
    return (
      <span className="subappalto">
        subappalto · {riga.fornitore}
        <button
          type="button"
          className="rimuovi"
          disabled={inCorso}
          onClick={() => scegli(null)}
        >
          cambia
        </button>
      </span>
    );

  return (
    <span className="subappalto cerca-fornitore">
      subappalto ·
      <input
        type="search"
        placeholder="chi lo fa"
        aria-label={`Fornitore del subappalto di ${riga.descrizione}`}
        disabled={inCorso}
        value={frase}
        onChange={(e) => setFrase(e.target.value)}
      />
      {trovati.length > 0 && (
        <ul className="trovati">
          {trovati.map((f) => (
            <li key={f.codiceMago}>
              <button type="button" onClick={() => scegli(f)}>
                {f.denominazione}
                <em className="da">{f.codiceMago}</em>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

/*  Se si dorme fuori, a tre stati.

    «Auto» non e' un valore in piu' per completezza: e' la differenza fra «non ci ho
    pensato, decidi tu dai chilometri» e «ho guardato e ho deciso che si torna a casa».
    La seconda e' un'informazione, e una spunta spenta la butterebbe via. E' lo stesso
    ragionamento dei viaggi e dell'importo forzato, che qui accanto hanno il campo vuoto
    al posto dell'auto.  */
type Pernotto = 'auto' | 'si' | 'no';

function pernottoScritto(t: TrasfertaLetta): Pernotto {
  if (!t.pernottoDeciso) return 'auto';
  return t.pernotta ? 'si' : 'no';
}

/*  Il cantiere e la squadra: i due dati da cui escono le trasferte.

    I passaggi sono in chiaro — giorni, giornate uomo, viaggi — perché una previsione va
    mostrata smontata o non si può discutere. Sul rapportino i chilometri sono un fatto:
    qualcuno ha guidato. Qui sono un'ipotesi, e chi la legge deve poter dire «no, in
    furgone ci andiamo insieme» e correggere il numero dei viaggi.

    La mappa è un iframe della Embed API di Google, che vuole l'indirizzo come testo:
    nessuna coordinata da chiedere, da pagare e da conservare.  */
function CantiereESquadra({
  preventivo: p,
  inCorso,
  azioni,
}: {
  preventivo: DettaglioPreventivo;
  inCorso: boolean;
  azioni?: AzioniPreventivo;
}) {
  const t = p.conti.trasferta;

  const [indirizzo, setIndirizzo] = useState(p.indirizzoCantiere ?? '');
  const [km, setKm] = useState(p.kmTrasferta === null ? '' : String(p.kmTrasferta));
  const [persone, setPersone] = useState(String(p.personeInSquadra));
  const [orePerGiornata, setOrePerGiornata] = useState(String(p.orePerGiornata));
  const [mezzi, setMezzi] = useState(String(p.mezziPerGiornata));
  const [viaggi, setViaggi] = useState(p.viaggiPrevisti === null ? '' : String(p.viaggiPrevisti));
  const [nellOra, setNellOra] = useState(p.trasfertaNelCostoOrario);
  const [forzato, setForzato] = useState(
    p.importoTrasfertaForzato === null ? '' : String(p.importoTrasfertaForzato),
  );
  /*  Il pernotto e' a tre stati e non una spunta: «lascia decidere ai chilometri» non e'
      la stessa cosa di «ho deciso che non si dorme fuori». Una spunta spenta le
      confonderebbe, ed e' proprio la distinzione che serve — la seconda e' una decisione
      del tecnico, la prima e' il caso normale.  */
  const [pernotto, setPernotto] = useState(pernottoScritto(t));
  /*  I COSTI DEI PASTI E DELLA NOTTE, scrivibili dal 21/09/2026. Prima stavano solo in
      configurazione, e la trasferta in Liguria dove il pranzo costava 20 invece di 15
      non si poteva scrivere. Restano congelati sul documento: qui si cambia questa
      offerta, non il parametro dell'azienda.  */
  /*  Il costo al chilometro, scrivibile su questa offerta dal 02/10/2026: «io
      aggiungerei una cella con il costo al chilometro che fa il calcolo con quella»
      (Andrea, in demo da Galizzi). Il valore per tutti sta nei parametri.  */
  const [tariffaKm, setTariffaKm] = useState(String(t.tariffaKm));
  const [costoPranzo, setCostoPranzo] = useState(String(p.costoPranzo));
  const [costoCena, setCostoCena] = useState(String(p.costoCena));
  const [costoNotte, setCostoNotte] = useState(String(p.costoPernottamento));
  const [albergo, setAlbergo] = useState(
    p.importoAlbergoForzato === null ? '' : String(p.importoAlbergoForzato),
  );
  const [messaggio, setMessaggio] = useState<string | null>(null);
  const [chiaveMappa, setChiaveMappa] = useState<string | null>(null);
  const [cantieri, setCantieri] = useState<CantiereMago[]>([]);

  /*  L'indirizzo su cui i chilometri a schermo sono stati calcolati. Serve a non
      richiamare Google quando si entra e si esce da un campo senza toccarlo: ogni
      chiamata e' una richiesta a pagamento.  */
  const ultimoCalcolato = useRef<string | null>(p.indirizzoCantiere ?? null);

  // La risposta del server sostituisce quello che c'è nei campi: si vede quello che è salvato.
  useEffect(() => {
    setIndirizzo(p.indirizzoCantiere ?? '');
    /*  Quello che torna dal server e' anche quello a cui i chilometri salvati si
        riferiscono: senza questo, rientrare nel campo dopo un salvataggio rifarebbe la
        stessa chiamata a Google per la stessa risposta.  */
    ultimoCalcolato.current = p.indirizzoCantiere ?? null;
    setKm(p.kmTrasferta === null ? '' : String(p.kmTrasferta));
    setPersone(String(p.personeInSquadra));
    setOrePerGiornata(String(p.orePerGiornata));
    setMezzi(String(p.mezziPerGiornata));
    setViaggi(p.viaggiPrevisti === null ? '' : String(p.viaggiPrevisti));
    setNellOra(p.trasfertaNelCostoOrario);
    setForzato(p.importoTrasfertaForzato === null ? '' : String(p.importoTrasfertaForzato));
    setPernotto(pernottoScritto(p.conti.trasferta));
    setTariffaKm(String(p.conti.trasferta.tariffaKm));
    setCostoPranzo(String(p.costoPranzo));
    setCostoCena(String(p.costoCena));
    setCostoNotte(String(p.costoPernottamento));
    setAlbergo(p.importoAlbergoForzato === null ? '' : String(p.importoAlbergoForzato));
  }, [p]);

  /*  I cantieri di questo cliente. Si chiedono una volta per cliente e non a ogni tasto:
      e' un elenco corto e fermo — le sedi di un'azienda non cambiano mentre si compila un
      preventivo — e non c'e' niente da cercare dentro.

      Se la chiamata fallisce non succede niente di visibile: la tendina non compare e
      l'indirizzo si scrive a mano, che e' come funzionava prima. Un errore rosso qui
      direbbe a chi compila che qualcosa si e' rotto proprio mentre non gli serve.  */
  useEffect(() => {
    let annullato = false;
    anagrafiche
      .cantieri(p.codiceClienteMago)
      .then((elenco) => !annullato && setCantieri(elenco))
      .catch(() => undefined);
    return () => {
      annullato = true;
    };
  }, [p.codiceClienteMago]);

  useEffect(() => {
    let annullato = false;
    commesse
      .configurazioneMappe()
      .then((c) => !annullato && setChiaveMappa(c.chiaveBrowser))
      // Senza chiave la mappa non si mostra: non è un errore da sbattere in faccia.
      .catch(() => undefined);
    return () => {
      annullato = true;
    };
  }, []);

  /*  I chilometri si ricalcolano DA SOLI quando l'indirizzo cambia.

      Prima bisognava premere «Calcola con Google», e il pulsante calcolava sull'indirizzo
      SALVATO: chi cambiava cantiere e premeva si portava a casa i chilometri di prima,
      che e' peggio di non calcolarli — il numero c'e', sembra giusto, e riguarda un altro
      posto. Adesso l'indirizzo viaggia con la richiesta e il calcolo parte da solo.

      Non a ogni tasto: ogni chiamata e' una richiesta a pagamento verso Google, e un
      indirizzo si scrive in quaranta battute. Parte quando si sceglie dalla tendina — un
      gesto solo, un indirizzo completo — e quando si esce dal campo scritto a mano.

      Ricalcola, non salva. Resta la regola di sempre: Google propone, il tecnico guarda e
      preme Salva. Un numero che diventa costo senza che nessuno l'abbia letto e' un
      errore d'indirizzo che si scopre a lavoro finito.  */
  async function calcola(indirizzoDaUsare?: string) {
    if (!azioni) return;
    const cercato = (indirizzoDaUsare ?? indirizzo).trim();
    setMessaggio(null);
    ultimoCalcolato.current = cercato;
    try {
      const esito = await azioni.calcolaKm(cercato === '' ? null : cercato);
      if (esito.kmAndataRitorno === null) setMessaggio(esito.problema ?? 'Calcolo non riuscito.');
      else {
        setKm(String(esito.kmAndataRitorno));
        setMessaggio(`${esito.kmAndataRitorno} km andata e ritorno da ${esito.sede}. Controlla e salva.`);
      }
    } catch (errore) {
      setMessaggio((errore as Error).message);
    }
  }

  /*  Il ricalcolo si chiede solo se l'indirizzo e' DAVVERO cambiato da quello su cui i
      chilometri sono stati calcolati l'ultima volta: entrare e uscire da un campo senza
      toccarlo non deve costare una chiamata.  */
  function ricalcolaSeCambiato(nuovo: string) {
    const pulito = nuovo.trim();
    if (pulito === '' || pulito === (ultimoCalcolato.current ?? '').trim()) return;
    void calcola(pulito);
  }

  function daiCampi(): CantierePreventivo {
    return {
      indirizzoCantiere: indirizzo.trim() === '' ? null : indirizzo.trim(),
      kmTrasferta: numeroDecimale(km),
      personeInSquadra: Math.trunc(numeroDecimale(persone) ?? 1),
      orePerGiornata: numeroDecimale(orePerGiornata) ?? 8,
      mezziPerGiornata: Math.trunc(numeroDecimale(mezzi) ?? 1),
      viaggi: viaggi.trim() === '' ? null : Math.trunc(numeroDecimale(viaggi) ?? 0),
      trasfertaNelCostoOrario: nellOra,
      /*  Campo vuoto è «lascia calcolare», uno zero scritto è «ho deciso che non si
          paga»: sono due cose diverse e vanno mandate diverse, o la concessione
          commerciale si confonde con la dimenticanza.  */
      importoTrasfertaForzato: forzato.trim() === '' ? null : (numeroDecimale(forzato) ?? 0),
      pernotta: pernotto === 'auto' ? null : pernotto === 'si',
      /*  I costi si mandano sempre: un campo svuotato torna a zero, che è una
          decisione — «qui il pranzo non lo paghiamo». Il totale dell'albergo invece
          segue la regola della trasferta forzata: vuoto è «lascia calcolare».  */
      tariffaKm: numeroDecimale(tariffaKm) ?? 0,
      costoPranzo: numeroDecimale(costoPranzo) ?? 0,
      costoCena: numeroDecimale(costoCena) ?? 0,
      costoPernottamento: numeroDecimale(costoNotte) ?? 0,
      importoAlbergoForzato: albergo.trim() === '' ? null : (numeroDecimale(albergo) ?? 0),
    };
  }

  /*  Lo stesso oggetto, ma letto dal documento salvato. Serve a sapere se c'e' qualcosa
      da mandare, e sta accanto a `daiCampi` apposta: sono due letture della stessa
      scheda, e tenerle lontane vorrebbe dire che fra sei mesi una ha un campo in piu'
      dell'altra e il salvataggio automatico smette di accorgersi di quel campo. Il tipo
      le obbliga a restare uguali — aggiungerne uno solo di qua non compila.  */
  function comeSalvato(): CantierePreventivo {
    const scritto = pernottoScritto(t);

    return {
      indirizzoCantiere: p.indirizzoCantiere,
      kmTrasferta: p.kmTrasferta,
      personeInSquadra: p.personeInSquadra,
      orePerGiornata: p.orePerGiornata,
      mezziPerGiornata: p.mezziPerGiornata,
      viaggi: p.viaggiPrevisti,
      trasfertaNelCostoOrario: p.trasfertaNelCostoOrario,
      importoTrasfertaForzato: p.importoTrasfertaForzato,
      pernotta: scritto === 'auto' ? null : scritto === 'si',
      tariffaKm: t.tariffaKm,
      costoPranzo: p.costoPranzo,
      costoCena: p.costoCena,
      costoPernottamento: p.costoPernottamento,
      importoAlbergoForzato: p.importoAlbergoForzato,
    };
  }

  /*  `modifiche` perche' una spunta o una tendina si salvano nel momento in cui si
      scelgono, e in quel momento lo stato di React non e' ancora cambiato: il valore
      nuovo arriva da qui invece che da una casella che sta per diventare un'altra cosa.  */
  function salva(modifiche: Partial<CantierePreventivo> = {}) {
    if (!azioni) return;
    setMessaggio(null);
    azioni.impostaCantiere({ ...daiCampi(), ...modifiche });
  }

  /*  IL CONTO SI RIFA' QUANDO SI ESCE DALLA CASELLA, non solo premendo Salva.

      Chiesto da Andrea in demo da Galizzi il 01/10/2026, dopo esserci cascato lui:
      cambiate le ore per giornata da 8 a 10, la riga sotto continuava a dire «8 giorni,
      8 viaggi». Non era rotta — legge il documento salvato — ma sta sotto le caselle e
      ha tutta l'aria di essere viva, e una previsione che si contraddice con quello che
      hai appena scritto sopra non la crede piu' nessuno.

      Si salva uscendo dal campo e non a ogni tasto: a ogni tasto vorrebbe dire una
      scrittura e un ricalcolo per ogni cifra di «82», e il numero a meta' — «8» — e'
      un'ipotesi che nessuno ha fatto. Uscire dal campo e' il momento in cui quello che
      c'e' scritto e' quello che si intendeva.

      E solo se e' DAVVERO cambiato qualcosa: entrare e uscire da sette caselle per
      guardarle non deve lasciare sette scritture nel registro delle modifiche di un
      preventivo. Mentre una scrittura e' in volo non se ne manda un'altra — la risposta
      riscrive i campi, e due salvataggi accavallati si sovrascriverebbero a vicenda.

      Dal 04/10/2026 il tasto Salva non c'e' piu' (punto 3.3 della riunione). Faceva
      anche un'altra cosa — dire senza provarci che quello che si vede e' quello che e'
      scritto — e quella la fa adesso `StatoScrittura`, qui sotto.  */
  function salvaSeCambiato() {
    if (!azioni || inCorso || allineato) return;

    salva();
  }

  const allineato = JSON.stringify(daiCampi()) === JSON.stringify(comeSalvato());

  const indirizzoMostrato = azioni ? indirizzo.trim() : (p.indirizzoCantiere ?? '');

  return (
    <SezioneChiudibile classe="cantiere" titolo="Cantiere e trasferte">

      {azioni ? (
        <div className="campi">
          {/*  Due file come nei parametri, e per lo stesso motivo: l'indirizzo di un
               cantiere e' una frase — «Via Brescia 118/A, 25086 Rezzato BS» — e in una
               fila sola restava largo come «Mezzi al giorno».  */}
          <div className="fila testi" onBlur={salvaSeCambiato}>
          {/*  I cantieri gia' visti su questo cliente.

               La tendina SCRIVE nel campo qui sotto e non lo sostituisce: sceglierne uno e
               poi correggergli il civico deve restare possibile, perche' la meta' delle
               volte il cantiere e' «lo stabilimento di Cameri, ma il cancello sul retro».
               Un campo di sola scelta costringerebbe a farsi aggiungere una filiale in
               Mago per scrivere un preventivo, e a quel punto si scriverebbe altrove.

               Compare solo se c'e' qualcosa da scegliere: una tendina vuota e' una promessa
               che non mantiene.  */}
          {cantieri.length > 0 && (
            <label>
              <span>Cantieri di questo cliente</span>
              <select
                className="campo"
                value=""
                disabled={inCorso}
                onChange={(e) => {
                  if (e.target.value === '') return;
                  setIndirizzo(e.target.value);
                  ricalcolaSeCambiato(e.target.value);
                }}
              >
                <option value="">Scegli, oppure scrivi l’indirizzo qui sotto…</option>
                {cantieri.map((c) => (
                  <option key={`${c.origine}-${c.codice ?? ''}-${c.indirizzoCompleto}`} value={c.indirizzoCompleto ?? ''}>
                    {c.indirizzoCompleto}
                    {c.origine === 'Storico' ? ' · già usato' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label>
            <span>Indirizzo del cantiere</span>
            <input
              className="campo"
              value={indirizzo}
              placeholder="Via, numero, CAP, città e provincia"
              onChange={(e) => setIndirizzo(e.target.value)}
              onBlur={(e) => ricalcolaSeCambiato(e.target.value)}
            />
          </label>
          </div>

          {/*  L'ascolto sta sul gruppo e non su ogni casella: `onBlur` in React e'
               `focusout`, che risale, quindi una riga sola copre tutte e dodici — e
               soprattutto copre anche la tredicesima, quella che qualcuno aggiungera' qui
               dentro fra un mese senza sapere che c'era un salvataggio da agganciare.  */}
          <div className="fila numeri" onBlur={salvaSeCambiato}>
          <label className="stretto">
            <span>Km andata e ritorno</span>
            <input
              className="campo"
              inputMode="decimal"
              value={km}
              onChange={(e) => setKm(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Costo al km €</span>
            <input
              className="campo"
              inputMode="decimal"
              aria-label="Costo al chilometro"
              value={tariffaKm}
              onChange={(e) => setTariffaKm(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Persone in squadra</span>
            <input
              className="campo"
              inputMode="numeric"
              value={persone}
              onChange={(e) => setPersone(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Ore per giornata</span>
            <input
              className="campo"
              inputMode="decimal"
              value={orePerGiornata}
              onChange={(e) => setOrePerGiornata(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Mezzi al giorno</span>
            <input
              className="campo"
              inputMode="numeric"
              value={mezzi}
              onChange={(e) => setMezzi(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Viaggi</span>
            <input
              className="campo"
              inputMode="numeric"
              value={viaggi}
              // Il segnaposto è il calcolo: si scrive qui solo per smentirlo.
              placeholder={String(t.viaggiCalcolati)}
              onChange={(e) => setViaggi(e.target.value)}
            />

            {/*  Quando i viaggi sono scritti a mano, sotto si legge quanti ne diceva il
                 calcolo. Il numero corretto da solo non dice niente: fra sei mesi nessuno
                 sa piu' se quell'uno e' una decisione o una svista, e con accanto il
                 calcolato si legge per quello che e' — qualcuno ha guardato il conto e ha
                 deciso diversamente.

                 Solo quando i due numeri differiscono davvero: scrivere «dato modificato,
                 il calcolato era 4» accanto a un 4 scritto a mano e' rumore.  */}
            {t.viaggiCorretti && t.viaggi !== t.viaggiCalcolati && (
              <em className="corretto">
                dato modificato: il calcolato era {t.viaggiCalcolati}
              </em>
            )}
          </label>

          {/*  L'importo deciso a mano scavalca tutto quello che sta sopra, zero
               compreso: «per un'ora di lavoro non ti faccio pagare di nuovo 40 €». Il
               conto continua a vedersi qui sotto, così si sa da cosa ci si sta
               discostando.  */}
          <label className="stretto">
            <span>Trasferta forzata €</span>
            <input
              className="campo"
              inputMode="decimal"
              aria-label="Importo di trasferta forzato"
              placeholder={String(t.importoCalcolato)}
              value={forzato}
              onChange={(e) => setForzato(e.target.value)}
            />
          </label>

          {/*  I COSTI DEI PASTI E DELLA NOTTE. Tre caselle in più, e in riunione era
               chiaro che il prezzo si paga volentieri: «riempiamo di caselline, lo so,
               però diventa molto più intuitivo capire i passaggi, perché sennò uno che
               arriva e non conosce il programma dice: come lo inserisco?».

               Sono i costi di QUESTA offerta, non del parametro: restano com'erano
               anche se domani l'azienda alza il rimborso.  */}
          <label className="stretto">
            <span>Pranzo €</span>
            <input
              className="campo"
              inputMode="decimal"
              aria-label="Costo di un pranzo"
              value={costoPranzo}
              onChange={(e) => setCostoPranzo(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Cena €</span>
            <input
              className="campo"
              inputMode="decimal"
              aria-label="Costo di una cena"
              value={costoCena}
              onChange={(e) => setCostoCena(e.target.value)}
            />
          </label>

          <label className="stretto">
            <span>Albergo €/notte</span>
            <input
              className="campo"
              inputMode="decimal"
              aria-label="Costo di una notte in albergo"
              value={costoNotte}
              onChange={(e) => setCostoNotte(e.target.value)}
            />
          </label>

          {/*  Il totale a forfait: «€250» detto in riunione era il preventivo
               dell'albergo, non un conto per notte, e capita ogni volta che la
               prenotazione è già stata fatta. Vuoto lascia il conto a notte, e il
               calcolato resta nel segnaposto.  */}
          <label className="stretto">
            <span>Albergo totale €</span>
            <input
              className="campo"
              inputMode="decimal"
              aria-label="Totale dell'albergo forzato"
              placeholder={String(t.costoAlbergoCalcolato)}
              value={albergo}
              onChange={(e) => setAlbergo(e.target.value)}
            />
          </label>

          {/*  Il pernotto compare solo se l'azienda ha configurato quanto costa una cena:
               dove non c'e' un importo non c'e' niente da decidere, e un campo che non
               cambia nessun numero e' un campo che insegna a non guardare i campi.  */}
          {t.costoCena > 0 && (
            /*  `scelta` e non `stretto` e basta: la prima voce e' «Come dice il calcolo
                (sì)», che in 140 px non ci sta e si legge «Come dice il calc…».  */
            <label className="stretto scelta">
              <span>Si dorme fuori</span>
              <select
                className="campo"
                value={pernotto}
                onChange={(e) => {
                  const scelto = e.target.value as Pernotto;
                  setPernotto(scelto);
                  salva({ pernotta: scelto === 'auto' ? null : scelto === 'si' });
                }}
              >
                <option value="auto">
                  Come dice il calcolo ({t.pernottoCalcolato ? 'sì' : 'no'})
                </option>
                <option value="si">Sì</option>
                <option value="no">No</option>
              </select>
            </label>
          )}
          </div>

          <label className="spunta">
            <input
              type="checkbox"
              checked={nellOra}
              onChange={(e) => {
                setNellOra(e.target.checked);
                salva({ trasfertaNelCostoOrario: e.target.checked });
              }}
            />
            <span>
              Metti la trasferta nel costo dell'ora — sparisce dalle voci e alza la
              tariffa; il totale non cambia
            </span>
          </label>

          {/*  Resta il solo «Calcola con Google», che non e' un salvataggio: e' un gesto
               che va chiesto, perche' chiama fuori e perche' riscrive i chilometri che
               magari qualcuno aveva corretto a mano.  */}
          <div className="comandi">
            <button type="button" onClick={() => void calcola()} disabled={inCorso}>
              Calcola con Google
            </button>
            <StatoScrittura inCorso={inCorso} allineato={allineato} />
          </div>
        </div>
      ) : (
        <p className="dettaglio">
          {p.indirizzoCantiere ?? 'Indirizzo non indicato'}
          {' · '}
          {p.kmTrasferta === null
            ? 'chilometri non stabiliti'
            : `${formattaNumero(p.kmTrasferta)} km andata e ritorno`}
          {' · '}
          {t.personeInSquadra === 1 ? 'una persona' : `${t.personeInSquadra} persone`}
        </p>
      )}

      {/*  Il conto smontato: è la parte che permette di dire «no, quei viaggi sono tre».  */}
      <p className="nota conto-trasferta">
        {t.giorni === 0 && !t.importoForzato ? (
          <>Nessuna ora sul preventivo: finché non ci sono righe, non ci sono giorni di cantiere.</>
        ) : (
          <>
            {formattaNumero(p.conti.oreTotali)} ore
            {t.personeInSquadra > 1 && <> in {t.personeInSquadra}</>} fanno {t.giorni}{' '}
            {t.giorni === 1 ? 'giorno' : 'giorni'} di cantiere
            {t.mezziPerGiornata > 1 && <> con {t.mezziPerGiornata} mezzi</>}: {t.viaggi}{' '}
            {t.viaggi === 1 ? 'viaggio' : 'viaggi'}
            {t.viaggiCorretti && ' (corretti a mano)'}
            {t.kmAndataRitorno !== null && (
              <>
                {' '}
                da {formattaNumero(t.kmAndataRitorno)} km a{' '}
                {formattaImporto(t.tariffaKm)} = {formattaImporto(t.costoKm)}
              </>
            )}
            {t.indennitaGiornaliera > 0 && (
              <>
                , più {t.giornateUomo} {t.giornateUomo === 1 ? 'giornata' : 'giornate'} da{' '}
                {formattaImporto(t.indennitaGiornaliera)} di indennità ={' '}
                {formattaImporto(t.costoIndennita)}
              </>
            )}
            {/*  I pasti si leggono con accanto il perche': «2 pranzi» da solo fa
                 chiedere perche' due e non tre, e la risposta — la distanza e le
                 giornate — è quella che permette di correggere il campo giusto.  */}
            {t.costoPasti > 0 && (
              <>
                , più{' '}
                {t.pranzi > 0 && (
                  <>
                    {t.pranzi} {t.pranzi === 1 ? 'pranzo' : 'pranzi'} da{' '}
                    {formattaImporto(t.costoPranzo)} (il cantiere è a{' '}
                    {formattaNumero(t.distanzaSolaAndata)} km)
                  </>
                )}
                {t.pranzi > 0 && t.cene > 0 && ' e '}
                {t.cene > 0 && (
                  <>
                    {t.cene} {t.cene === 1 ? 'cena' : 'cene'} da{' '}
                    {formattaImporto(t.costoCena)} (
                    {t.pernottoDeciso ? 'si dorme fuori, deciso a mano' : 'si dorme fuori'})
                  </>
                )}{' '}
                = {formattaImporto(t.costoPasti)}
              </>
            )}
            {/*  L'albergo, che fino al 21/09/2026 non c'era: si dormiva fuori, si
                 pagava la cena, e il letto non lo pagava nessuno.  */}
            {t.costoAlbergo > 0 && (
              <>
                , più{' '}
                {t.albergoForzato ? (
                  <>l'albergo fissato a {formattaImporto(t.costoAlbergo)}</>
                ) : (
                  <>
                    {pernottamenti(t)} d'albergo da{' '}
                    {formattaImporto(t.costoPernottamento)} ={' '}
                    {formattaImporto(t.costoAlbergo)}
                  </>
                )}
              </>
            )}
            .{' '}
            {t.importoForzato ? (
              <>
                Il calcolo direbbe {formattaImporto(t.importoCalcolato)}, ma è stato
                fissato a <strong>{formattaImporto(t.importo)}</strong>.
              </>
            ) : (
              <>
                Totale <strong>{formattaImporto(t.importo)}</strong>
                {p.trasfertaNelCostoOrario && (
                  <>
                    , che qui non si vede come voce: sta nel costo dell'ora, a{' '}
                    {formattaImporto(p.conti.supplementoOrario)} in più ciascuna
                  </>
                )}
                .
              </>
            )}
          </>
        )}
      </p>

      {/*  LA NOTA INTERNA, chiesta in riunione il 14/09: «il costo della trasferta
           spalmato su ogni ora di manodopera».

           E' la domanda «questo cantiere lontano conviene?», e la risposta non si legge
           dal totale della trasferta: 264 € non dicono niente finche' non si sa su
           quante ore si spalmano. Tre euro e mezzo sull'ora, accanto ai 41 della
           tariffa, si leggono in un colpo.

           Il numero arriva dal server anche quando la trasferta NON sta nel costo
           orario, ed e' tutta la differenza con `supplementoOrario`, che vale zero
           finche' la spunta e' spenta perche' dice quanto si sta davvero caricando.
           Qui si dice quanto si caricherebbe.

           Non si scrive la percentuale sulla tariffa, che pure verrebbe comoda: sarebbe
           un conto fatto qui, e a fianco di un numero del server un numero nostro non si
           distingue. I due importi accostati dicono la stessa cosa e non mentono.

           Solo a spunta spenta: con la spunta accesa la frase qui sopra lo dice gia',
           e ripeterlo due volte fa cercare la differenza fra due numeri uguali.

           «Interna» e' la parola della riunione, e vuol dire una cosa precisa: **non ha
           un segnaposto di stampa**, quindi in offerta non ci puo' finire nemmeno per
           sbaglio.  */}
      {!p.trasfertaNelCostoOrario && p.conti.trasfertaPerOra > 0 && (
        <p className="nota interna">
          <strong>Nota interna</strong> — non va in offerta: la trasferta pesa{' '}
          {formattaImporto(p.conti.trasfertaPerOra)} su ognuna delle{' '}
          {formattaNumero(p.conti.oreTotali)} ore, con la manodopera a{' '}
          {formattaImporto(p.tariffaManodoperaOraria)} l'ora.
        </p>
      )}

      {messaggio && <p className="nota">{messaggio}</p>}

      {indirizzoMostrato !== '' &&
        (chiaveMappa ? (
          <iframe
            className="mappa"
            title={`Mappa di ${indirizzoMostrato}`}
            loading="lazy"
            /*  La chiave del browser è ristretta per referrer sulla console di Google:
                perché la restrizione funzioni, il referrer deve partire.  */
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(
              chiaveMappa,
            )}&q=${encodeURIComponent(indirizzoMostrato)}&language=it`}
          />
        ) : (
          <p className="nota">
            La mappa non si vede finché non è configurata la chiave di Google
            (<code>Mappe:ChiaveBrowser</code>).
          </p>
        ))}
    </SezioneChiudibile>
  );
}

/*  LA SCALA DEGLI ONERI DELLA SICUREZZA, detta in riunione il 01/10/2026: «il
    gestionale vada in percentuale di default il 2%, poi si puo' modificare 1 2 3 5 8».

    Cinque numeri e non un campo libero perche' sono quelli che Galizzi usa davvero, e
    una tendina corta si sceglie con un gesto mentre un campo libero si batte — e si
    sbaglia. Un valore gia' scritto fuori da questa scala resta dov'e': vedi dove si usa.

    Sta nel codice e non nei parametri di partenza perche' e' la scala del mestiere e
    non un valore d'azienda; quello che cambia da azienda ad azienda — il 2% di
    partenza — sta gia' nei parametri.  */
const PERCENTUALI_ONERI = [1, 2, 3, 5, 8];

const TIPI_SPESA: { valore: string; etichetta: string }[] = [
  { valore: 'Sicurezza', etichetta: 'Oneri della sicurezza' },
  { valore: 'Progettazione', etichetta: 'Progettazione' },
  { valore: 'Pasti', etichetta: 'Pasti' },
  { valore: 'Altro', etichetta: 'Altra spesa' },
];

/*  Le spese che non stanno su nessuna riga e che nessuno si ricorda di aggiungere.

    Le trasferte non sono in questo elenco e non si tolgono da qui: si calcolano dal
    cantiere e dalle ore, quindi salvarne l'importo vorrebbe dire tenere un numero che
    smette di essere vero appena si aggiunge una riga. Nel riepilogo compaiono comunque
    insieme alle altre, perché a chi guarda interessa il blocco.

    Ogni voce è un importo OPPURE una percentuale. La percentuale è sul costo diretto e
    non sul prezzo: gli oneri della sicurezza sono una quota del lavoro da fare, non del
    ricarico che ci si mette sopra.  */
function SpeseAccessorie({
  preventivo: p,
  inCorso,
  azioni,
}: {
  preventivo: DettaglioPreventivo;
  inCorso: boolean;
  azioni?: AzioniPreventivo;
}) {
  const [tipo, setTipo] = useState('Sicurezza');
  const [descrizione, setDescrizione] = useState('');
  const [importo, setImporto] = useState('');
  const [percentuale, setPercentuale] = useState('');

  if (p.spese.length === 0 && !azioni) return null;

  function aggiungi() {
    if (!azioni) return;
    const valoreImporto = numeroDecimale(importo);
    const valorePercentuale = numeroDecimale(percentuale);
    if (valoreImporto === null && valorePercentuale === null) return;

    azioni.aggiungiSpesa({
      tipo,
      descrizione: descrizione.trim() === '' ? null : descrizione.trim(),
      // Se sono valorizzati tutti e due vince l'importo: il server rifiuterebbe la coppia,
      // e un errore su una scelta che l'interfaccia poteva fare da sola è pigrizia.
      importo: valoreImporto,
      percentuale: valoreImporto !== null ? null : valorePercentuale,
    });

    setDescrizione('');
    setImporto('');
    setPercentuale('');
  }

  return (
    <SezioneChiudibile classe="spese-accessorie" titolo="Spese accessorie">

      {/*  COSA IL CLIENTE LEGGE DI QUELLO CHE NON STA NELLE RIGHE.

           Fino al 28/09/2026 niente, e il riepilogo dell'offerta non faceva il Totale:
           su un preventivo vero mancavano 1.049 € su 3.269 — trasferte, accessorie,
           costi generali e il ricarico — che non comparivano da nessuna parte.

           Adesso si sceglie. Quello che non si dichiara non sparisce: si spalma sui
           capitoli dentro «Manodopera e minuteria», e il Totale resta identico. Cambia
           solo quanto se ne racconta, ed e' per questo che le spunte stanno qui e non
           fra i parametri economici — non toccano un euro.  */}
      {azioni && (
        <p className="dichiarazioni">
          {/*  Tre strade e non una spunta, dal 02/10/2026: oltre a tacerla o a
               scriverla in fondo, la trasferta si puo' ripartire dentro i capitoli in
               proporzione alle ore. Un elenco a tendina e non tre bottoni, perche' le
               scelte sono alternative e la riga qui accanto ne ha gia' una a spunta: due
               modi di dire «scegli» uno sotto l'altro si leggono peggio di uno solo.  */}
          <label className="scelta">
            la trasferta in offerta
            <select
              value={p.trasfertaInOfferta}
              disabled={inCorso}
              onChange={(e) =>
                azioni.dichiarazioni(
                  e.target.value as TrasfertaInOfferta,
                  p.costiGeneraliVisibiliInStampa,
                )
              }
            >
              <option value="Nascosta">non si nomina</option>
              <option value="VoceUnica">una voce nel riepilogo</option>
              <option value="NeiCapitoli">dentro i capitoli, a ore</option>
            </select>
          </label>
          <label className="spunta">
            <input
              type="checkbox"
              checked={p.costiGeneraliVisibiliInStampa}
              disabled={inCorso}
              onChange={(e) =>
                azioni.dichiarazioni(p.trasfertaInOfferta, e.target.checked)
              }
            />
            i costi generali si leggono in offerta
          </label>
          <span className="nota">
            Quello che non si dichiara finisce nei capitoli come «Manodopera e minuteria».
            Il Totale non cambia.
          </span>
        </p>
      )}

      {p.spese.length === 0 ? (
        <p className="nota">
          Nessuna spesa accessoria. Sono le voci che fanno la differenza fra un preventivo
          che regge e uno che sembra aver guadagnato.
        </p>
      ) : (
        <table className="confronto">
          <tbody>
            {p.spese.map((spesa) => (
              <tr key={spesa.id}>
                <td>
                  <span className="nome">{spesa.nome}</span>
                  <span className="dettaglio">
                    {/*  LA PERCENTUALE DEGLI ONERI SI CAMBIA DA QUI, e fra i valori che
                         Galizzi usa: «il gestionale vada in percentuale di default il
                         2%, poi si puo' modificare 1 2 3 5 8» (01/10/2026, punto 1.1).
                         Il 2% di partenza c'era gia'; cambiarlo voleva dire togliere la
                         voce e riscriverla.

                         Solo sugli ONERI: quella scala e' la loro: un'altra spesa a
                         percentuale — una provvigione, uno smaltimento — puo' valere
                         qualunque numero, e offrirle cinque scelte sarebbe una gabbia.

                         Il valore scritto resta in elenco se non e' fra i cinque: una
                         voce al 2,5% deciso da qualcuno non deve diventare 2 perche'
                         qualcun altro ha aperto la tendina.  */}
                    {azioni && spesa.percentuale !== null && spesa.tipo === 'Sicurezza' ? (
                      <>
                        <select
                          aria-label={`Percentuale di ${spesa.nome}`}
                          value={String(spesa.percentuale)}
                          disabled={inCorso}
                          onChange={(e) =>
                            azioni.modificaSpesa(spesa.id, {
                              tipo: spesa.tipo,
                              descrizione: spesa.descrizione,
                              importo: null,
                              percentuale: Number(e.target.value),
                              nelTotale: spesa.nelTotale,
                              visibileInStampa: spesa.visibileInStampa,
                            })
                          }
                        >
                          {!PERCENTUALI_ONERI.includes(spesa.percentuale) && (
                            <option value={String(spesa.percentuale)}>
                              {formattaPercentuale(spesa.percentuale)}
                            </option>
                          )}
                          {PERCENTUALI_ONERI.map((v) => (
                            <option key={v} value={String(v)}>
                              {v}%
                            </option>
                          ))}
                        </select>{' '}
                        sul costo diretto
                      </>
                    ) : spesa.percentuale !== null
                      ? `${formattaPercentuale(spesa.percentuale)} sul costo diretto`
                      : 'importo fisso'}

                    {/*  Sommata o «di cui»: è la differenza fra «il cliente paga anche
                         questo» e «questo è già dentro, e si dichiara». Si legge sempre,
                         anche a preventivo chiuso, perché e' quello che spiega un totale
                         che non torna a chi lo somma a mano.  */}
                    {azioni ? (
                      <select
                        aria-label={`Come conta ${spesa.nome}`}
                        value={spesa.nelTotale ? 'somma' : 'dicui'}
                        disabled={inCorso}
                        onChange={(e) =>
                          azioni.modificaSpesa(spesa.id, {
                            tipo: spesa.tipo,
                            descrizione: spesa.descrizione,
                            importo: spesa.importo,
                            percentuale: spesa.percentuale,
                            nelTotale: e.target.value === 'somma',
                          })
                        }
                      >
                        <option value="somma">si somma al totale</option>
                        <option value="dicui">di cui del totale, solo da esporre</option>
                      </select>
                    ) : (
                      <em className={spesa.nelTotale ? '' : 'motivo'}>
                        {spesa.nelTotale ? 'si somma al totale' : 'di cui del totale'}
                      </em>
                    )}

                    {/*  SE IL CLIENTE LA LEGGE. Spenta — come nascono tutte — la voce sta
                         dentro il prezzo senza dirsi: la sua quota si spalma sui capitoli
                         dentro «Manodopera e minuteria», e il Totale non cambia di un
                         euro. Cambia solo quanto se ne racconta.

                         Solo sulle voci sommate: un «di cui» e' gia' fuori dal giro —
                         non entra nel totale, quindi non c'e' niente da spalmare, e in
                         offerta si dichiara comunque.  */}
                    {azioni && spesa.nelTotale && (
                      <label className="spunta">
                        <input
                          type="checkbox"
                          checked={spesa.visibileInStampa}
                          disabled={inCorso}
                          onChange={(e) =>
                            azioni.modificaSpesa(spesa.id, {
                              tipo: spesa.tipo,
                              descrizione: spesa.descrizione,
                              importo: spesa.importo,
                              percentuale: spesa.percentuale,
                              nelTotale: spesa.nelTotale,
                              visibileInStampa: e.target.checked,
                            })
                          }
                        />
                        si legge in offerta
                      </label>
                    )}
                  </span>
                </td>
                <td className={`destra${spesa.nelTotale ? '' : ' aggregato'}`}>
                  {formattaImporto(spesa.importoCalcolato)}
                </td>
                {azioni && (
                  <td className="destra">
                    <button
                      type="button"
                      className="rimuovi"
                      aria-label={`Togli ${spesa.nome}`}
                      disabled={inCorso}
                      onClick={() => azioni.rimuoviSpesa(spesa.id)}
                    >
                      <Icona nome="rimuovi" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {azioni && (
        <div className="nuova-spesa">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo di spesa">
            {TIPI_SPESA.map((t) => (
              <option key={t.valore} value={t.valore}>
                {t.etichetta}
              </option>
            ))}
          </select>
          <input
            placeholder="Descrizione (facoltativa)"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
          />
          <input
            placeholder="€"
            inputMode="decimal"
            aria-label="Importo della spesa"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
          />
          <input
            placeholder="%"
            inputMode="decimal"
            aria-label="Percentuale della spesa"
            value={percentuale}
            onChange={(e) => setPercentuale(e.target.value)}
          />
          <button type="button" disabled={inCorso} onClick={aggiungi}>
            Aggiungi
          </button>
        </div>
      )}

    </SezioneChiudibile>
  );
}

/*  Le righe, nell'ordine e coi capitoli.

    L'albero si disegna dal livello e non dal padre: la posizione è già ordinata dal
    server, e la profondità è un rientro. Il padre serve al dominio — per portare via le
    righe insieme al capitolo — non a chi guarda.  */
function Righe({
  preventivo: p,
  inCorso,
  azioni,
}: {
  preventivo: DettaglioPreventivo;
  inCorso: boolean;
  azioni?: AzioniPreventivo;
}) {
  const [nuovaDescrizione, setNuovaDescrizione] = useState('');
  const [nuovoLivello, setNuovoLivello] = useState('3');
  const [sblocco, setSblocco] = useState<EsitoSbloccoPrezzi | null>(null);
  const [erroreSblocco, setErroreSblocco] = useState<string | null>(null);

  const ordinate = [...p.righe].sort((a, b) => a.posizione - b.posizione);
  const bloccate = ordinate.filter((r) => r.prezzoBloccato).length;

  async function sbloccaPrezzi() {
    if (!azioni) return;
    setErroreSblocco(null);
    setSblocco(null);
    try {
      setSblocco(await azioni.sbloccaPrezzi());
    } catch (e) {
      setErroreSblocco((e as Error).message);
    }
  }

  /*  Il capitolo di appartenenza è l'ultimo capitolo aperto sopra la coda: è quello che
      si aspetta chi scrive una riga sotto un titolo, e resta correggibile spostandola.
      Sta qui e non dentro `aggiungi` perché adesso lo usano in due — il modulo a mano e
      il catalogo — e due copie della stessa regola prima o poi mandano le due righe in
      due capitoli diversi.  */
  const capitoloDiCoda = [...ordinate].reverse().find((r) => r.livello < 3);

  /*  Lo stesso articolo a due prezzi diversi dentro lo stesso preventivo. Si calcola qui
      e non riga per riga: la domanda riguarda l'insieme, e chiederla ottanta volte
      vorrebbe dire ottanta giri sull'elenco per la stessa risposta.  */
  const doppi = prezziDoppi(ordinate);
  const marcate = righeConPrezzoDoppio(ordinate);

  /*  DOVE FINISCE OGNI CAPITOLO: sotto quella riga va il suo «inserisci articolo».
      Solo i capitoli di primo livello, come ha chiesto Galizzi il 15/10/2026 — «in fondo
      a ogni capitolo» — e non i sottocapitoli: due barre di inserimento una sotto
      l'altra a ogni fine di sottocapitolo farebbero una tabella fatta di barre.  */
  /*  Quali barre sono aperte, per capitolo, e tenute QUI: la barra si sposta sotto la
      voce appena inserita, cioe' React la smonta e la rimonta, e se l'apertura vivesse
      dentro di lei si richiuderebbe a ogni articolo — proprio mentre se ne mettono dieci
      di fila nello stesso capitolo.  */
  const [capitoliAperti, setCapitoliAperti] = useState<Set<string>>(new Set());
  const apriCapitolo = (id: string, aperto: boolean) =>
    setCapitoliAperti((prima) => {
      const dopo = new Set(prima);
      if (aperto) dopo.add(id);
      else dopo.delete(id);
      return dopo;
    });

  /*  I CAPITOLI CHIUSI (15/10/2026): «restringo tutto, vedo i miei capitoli e apro solo
      quello che mi interessa». Si nasconde il blocco a schermo e basta: e' un modo di
      guardare, non cambia niente nel documento ne' in stampa, e non si ricorda fra una
      visita e l'altra — riaprendo il preventivo si vede tutto, che e' il caso sicuro.  */
  const [capitoliChiusi, setCapitoliChiusi] = useState<Set<string>>(new Set());
  const chiudiCapitolo = (id: string, chiuso: boolean) =>
    setCapitoliChiusi((prima) => {
      const dopo = new Set(prima);
      if (chiuso) dopo.add(id);
      else dopo.delete(id);
      return dopo;
    });

  /*  Da dove si puo' uscire (16/10/2026): una voce dentro un capitolo di primo livello
      no — fuori non avrebbe un padre, e conto e stampa la rimetterebbero dentro. La
      freccia non si mostra, invece di promettere uno spostamento che la stampa smentisce.
      La stessa regola sta nel dominio (`Preventivo.Sporgi`), che la fa valere.  */
  const perId = new Map(ordinate.map((r) => [r.id, r]));
  const puoUscire = (r: RigaPreventivoLetta) =>
    r.rigaPadreId !== null && !(r.livello === 3 && perId.get(r.rigaPadreId)?.livello === 1);

  const fineCapitolo = new Map<string, RigaPreventivoLetta[]>();
  const nascoste = new Set<string>();
  for (const capitolo of ordinate.filter((r) => r.livello === 1)) {
    const ultima = ultimaDelBlocco(capitolo, ordinate);
    fineCapitolo.set(ultima.id, [...(fineCapitolo.get(ultima.id) ?? []), capitolo]);

    if (capitoliChiusi.has(capitolo.id))
      for (const r of ordinate)
        if (r.posizione > capitolo.posizione && r.posizione <= ultima.posizione) nascoste.add(r.id);
  }

  function aggiungi() {
    if (!azioni || nuovaDescrizione.trim() === '') return;

    const livello = Number(nuovoLivello);
    const capitolo = [...ordinate].reverse().find((r) => r.livello < livello);

    azioni.aggiungiRiga({
      livello,
      descrizione: nuovaDescrizione.trim(),
      sottoRigaId: capitolo?.id ?? null,
    });
    setNuovaDescrizione('');
  }

  return (
    <FornitoreCarrello preventivoId={p.id}>
      {({ voci, aggiorna, errore: erroreCarrello }) => (
    <section className="righe-preventivo">
      <h3>
        {ordinate.length === 1 ? 'Una riga' : `${ordinate.length} righe`}
        {/*  RINUMERA, accanto al conteggio. I numeri d'ordine si consumano: inserisci tre
             volte fra 10 e 20 e ti ritrovi 11,25 e 11,875 — leggibili per il programma,
             non per chi deve scriverci dentro. Questo li rimette in decine senza muovere
             una riga.

             Non si fa da sola a ogni salvataggio, ed e' voluto: un numero battuto a mano
             deve restare quello che si e' battuto, altrimenti si scrive 11 e si rilegge
             20 senza capire perche'.  */}
        {azioni && ordinate.length > 1 && (
          <button
            type="button"
            className="rinumera"
            disabled={inCorso}
            title="Rimette i numeri d'ordine in decine, senza cambiare l'ordine"
            onClick={() => azioni.rinumeraRighe()}
          >
            Rinumera
          </button>
        )}
      </h3>

      {/*  COSA FA LA PRIMA COLONNA, scritto una volta sopra la tabella.

           Andrea, 20/09/2026: «se modifico l'ordinamento non cambia e non mi fa salvare».
           Provato: il numero si sposta eccome, e la chiamata parte. Quello che manca e'
           il gesto che la fa partire — Invio — e non lo dice niente.

           Il difetto vero e' che quel campo somiglia a tutti gli altri della riga, e tutti
           gli altri si salvano con «Salva la riga». Questo no, di proposito: spostare una
           riga e' una mossa sola e reversibile, e aspettare un secondo gesto per
           confermarla la lascerebbe a meta'. Ma se la regola e' diversa va detta, e
           dirla una volta sopra la tabella costa una riga invece che una per riga.  */}
      {azioni && ordinate.length > 1 && (
        <p className="nota nota-ordine">
          Per spostare una riga, scrivi il suo numero nella colonna <strong>Ord.</strong> e
          premi <kbd>Invio</kbd>. Va dove la porta il numero, con tutto quello che ha sotto.
        </p>
      )}

      {/*  Sbloccare e rileggere i listini sono un gesto solo: il blocco esiste proprio
           per tenere fuori i listini, quindi toglierlo e non rileggere lascerebbe righe
           libere ferme a un prezzo vecchio — il peggiore dei due mondi. Il comando
           compare solo se c'è qualcosa di bloccato, perché altrimenti non promette
           niente.  */}
      {azioni && bloccate > 0 && (
        <p className="nota">
          {bloccate === 1
            ? 'Una riga ha il costo bloccato: gli aggiornamenti di listino non la toccano.'
            : `${bloccate} righe hanno il costo bloccato: gli aggiornamenti di listino non le toccano.`}{' '}
          <button
            type="button"
            className="salva-riga"
            disabled={inCorso}
            onClick={() => void sbloccaPrezzi()}
          >
            Sblocca e rileggi i listini
          </button>
        </p>
      )}

      {erroreSblocco && <p className="errore">{erroreSblocco}</p>}

      {sblocco && (
        <div className="nota esito-sblocco">
          <p>
            {sblocco.righeSbloccate === 0
              ? 'Nessuna riga era bloccata.'
              : `${sblocco.righeSbloccate} ${sblocco.righeSbloccate === 1 ? 'riga sbloccata' : 'righe sbloccate'}.`}{' '}
            {sblocco.aggiornati.length === 0
              ? 'Nessun prezzo è cambiato: i listini dicono quello che c’era già.'
              : `${sblocco.aggiornati.length} ${sblocco.aggiornati.length === 1 ? 'prezzo aggiornato' : 'prezzi aggiornati'} dai listini.`}
            {sblocco.senzaListino > 0 && (
              <>
                {' '}
                {sblocco.senzaListino}{' '}
                {sblocco.senzaListino === 1 ? 'riga è rimasta' : 'righe sono rimaste'} al
                loro prezzo: non hanno un codice da cercare.
              </>
            )}
          </p>

          {/*  Riga per riga e non solo un totale: un prezzo concordato riscritto in
               silenzio si scopre dal cliente.  */}
          {sblocco.aggiornati.length > 0 && (
            <ul className="elenco-prezzi">
              {sblocco.aggiornati.map((a) => (
                <li key={a.rigaId}>
                  {a.descrizione}: {formattaImporto(a.prima)} → {formattaImporto(a.dopo)}{' '}
                  <em className={a.dopo > a.prima ? 'motivo' : ''}>
                    ({a.dopo > a.prima ? '+' : ''}
                    {formattaImporto(a.dopo - a.prima)})
                  </em>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/*  Lo stesso codice a due prezzi diversi: si segnala e non si impedisce.

           Due prezzi diversi sono a volte la cosa giusta — quantita' diverse, una posa
           piu' scomoda, una riga tenuta ferma da un accordo col fornitore — e un blocco
           costringerebbe ad aggirarlo cambiando il codice, che e' peggio del problema.
           Quello che non deve succedere e' che se ne accorga il cliente.  */}
      {doppi.length > 0 && (
        <div className="nota prezzi-doppi">
          <p>
            {doppi.length === 1
              ? 'Un articolo compare a due prezzi diversi:'
              : `${doppi.length} articoli compaiono a prezzi diversi:`}
          </p>
          <ul className="elenco-prezzi">
            {doppi.map((d) => (
              <li key={d.codice}>
                <em className="chiave">{d.codice}</em> {d.descrizione}:{' '}
                {d.prezzi.map((v) => formattaImporto(v)).join(' e ')} l'uno{' '}
                <em className="motivo">
                  (+{formattaImporto(d.prezzi[d.prezzi.length - 1]! - d.prezzi[0]!)})
                </em>
              </li>
            ))}
          </ul>
          <p className="dettaglio">
            Può essere giusto — quantità diverse, una posa più scomoda, un prezzo
            concordato — ma in offerta si legge.
          </p>
        </div>
      )}

      {/*  Il carrello sopra la tabella (15/10/2026): chi arriva in un preventivo nuovo
           con qualcosa da incollare lo vede prima di cominciare a scrivere.  */}
      <PannelloCarrello
        preventivo={p}
        voci={voci}
        errore={erroreCarrello}
        aggiorna={aggiorna}
        incolla={azioni?.incolla}
      />

      {ordinate.length === 0 ? (
        <p className="nota">
          Nessuna riga: il preventivo non ha ancora né un costo né un prezzo.
        </p>
      ) : (
        /*  LE OTTO COLONNE chieste il 15/09, e l'ordine non e' casuale: si legge da
            sinistra a destra come si costruisce il prezzo. Quanto ne serve, quanto
            costerebbe di listino, quanto lo paghiamo davvero, quanto fa in tutto, quante
            ore ci vogliono, quanto ci si mette sopra, e i due numeri che vede il cliente.

            Il listino lordo accanto al netto e' meta' del punto: «340 € meno il 42 % fa
            197,20» si controlla a colpo d'occhio, «197,20» da solo non si controlla
            affatto, e uno sconto caduto o applicato due volte si scopre dalla fattura.

            La tabella scorre in orizzontale sotto una certa larghezza invece di
            stringersi: nove colonne in seicento pixel vorrebbero dire una descrizione di
            dodici caratteri, che e' peggio di uno scorrimento.  */
        <div className="tabella-righe">
        <table className="confronto righe-albero">
          <thead>
            <tr>
              {/*  LA PRIMA COLONNA E' IL NUMERO D'ORDINE, e si scrive.

                   Chiesta da Andrea il 19/09/2026: «se qualcuno vuole spostare da riga
                   1100 a riga 11 lo fa velocemente senza dover usare i tastini, che sono
                   comodi quando le righe sono vicine». Le frecce restano — per scavalcare
                   la riga accanto sono il gesto piu' breve — ma su un preventivo da
                   ottanta voci portarne una dal fondo in testa vuol dire ottanta clic.

                   I numeri vanno di dieci in dieci proprio perche' si possa scrivere in
                   mezzo: fra 10 e 20 ci sta 11, e la riga ci va.  */}
              <th className="destra ordine" title="Numero d'ordine: scrivilo per spostare la riga">
                Ord.
              </th>
              <th>Voce</th>
              {/*  Due campi sotto un'intestazione sola erano una colonna sola in cui
                   nessuno sapeva dove scrivere. L'intestazione li nomina tutti e due.  */}
              <th className="destra" title="Quantità e unità di misura">Q.tà · UM</th>
              <th className="destra" title="Prezzo di listino lordo, unitario">Listino</th>
              <th className="destra" title="Prezzo d'acquisto scontato, unitario">Acquisto</th>
              <th className="destra" title="Prezzo d'acquisto scontato per la quantità">Tot. acq.</th>
              <th className="destra" title="Ore di manodopera per unità">Ore</th>
              {/*  La posa a corpo: un importo al posto di ore per tariffa. La colonna sta
                   accanto alle ore e non altrove perche' e' la stessa domanda — quanto
                   costa la manodopera di questa riga — e le due risposte vanno lette
                   vicine: le ore restano a dire quanto dura il cantiere.  */}
              <th className="destra" title="Posa a corpo: un importo fisso invece di ore per tariffa">
                Posa
              </th>
              {/*  Il ricarico in colonna e non nascosto in una scheda: è il numero su cui
                   si litiga, e in riunione era la cosa che si voleva vedere riga per riga.  */}
              <th className="destra" title="Ricarico applicato">Ric.</th>
              {/*  LO SCONTO AL CLIENTE, chiesto il 21/09/2026, e sta QUI: subito prima
                   dell'unitario, perché è la colonna che lo abbassa. «Prima dell'unità
                   cliente sarà già al netto, l'unitario lo vedremo scontato.»  */}
              <th className="destra" title="Sconto al cliente: scritto qui o ereditato dal capitolo">
                Sc. %
              </th>
              <th className="destra" title="Prezzo unitario al cliente, manodopera inclusa">
                Unit. cliente
              </th>
              <th className="destra" title="Totale della riga: ricarico e manodopera inclusi">
                Totale
              </th>
              {azioni && <th />}
            </tr>
          </thead>
          <tbody>
            {ordinate.map((riga) => nascoste.has(riga.id) ? null : (
              <Fragment key={riga.id}>
              <RigaLetta
                riga={riga}
                puoUscire={puoUscire(riga)}
                chiuso={capitoliChiusi.has(riga.id)}
                onChiuso={
                  riga.livello === 1 && ultimaDelBlocco(riga, ordinate).id !== riga.id
                    ? (c) => chiudiCapitolo(riga.id, c)
                    : undefined
                }
                /*  Quante voci stanno sotto questa testa. Serve alla frase accanto alla
                    scelta: una che ne chiude dodici e una che ne chiude una si guardano
                    diversamente, e il numero e' l'unica cosa che lo dice senza aprire
                    l'albero.  */
                sotto={discendenti(riga.id, ordinate)}
                prezzoDoppio={marcate.has(riga.id)}
                inCorso={inCorso}
                azioni={azioni}
              />
              {azioni &&
                (fineCapitolo.get(riga.id) ?? [])
                  .filter((capitolo) => !capitoliChiusi.has(capitolo.id))
                  .map((capitolo) => (
                  <InserisciNelCapitolo
                    key={`inserisci-${capitolo.id}`}
                    capitolo={capitolo}
                    dopo={riga}
                    aperto={capitoliAperti.has(capitolo.id)}
                    onAperto={(a) => apriCapitolo(capitolo.id, a)}
                    inCorso={inCorso}
                    azioni={azioni}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {azioni && (
        <div className="nuova-riga">
          {/*  «Inserisci capitolo / inserisci sottocapitolo / inserisci articolo»
               (Andrea, 01/10/2026). Prima c'era scritto solo «Capitolo», e un sostantivo
               da solo in una tendina non dice se sta descrivendo la riga che si sta per
               fare o filtrando quelle che ci sono gia'. Col verbo davanti la tendina e il
               bottone accanto si leggono come una frase sola: «inserisci articolo —
               aggiungi».

               Il bottone resta separato: l'ipotesi di un pulsante unico che fa tutto e'
               stata scartata in riunione, perche' il livello si sceglie piu' spesso di
               quanto si creda — sotto un capitolo ci vanno sia sottocapitoli sia
               articoli — e un pulsante solo lo nasconderebbe dentro un menu.  */}
          <select
            value={nuovoLivello}
            onChange={(e) => setNuovoLivello(e.target.value)}
            aria-label="Cosa inserire"
          >
            <option value="1">Inserisci capitolo</option>
            <option value="2">Inserisci sottocapitolo</option>
            <option value="3">Inserisci articolo</option>
          </select>
          <input
            placeholder="Descrizione della voce"
            value={nuovaDescrizione}
            onChange={(e) => setNuovaDescrizione(e.target.value)}
          />
          <button type="button" disabled={inCorso} onClick={aggiungi}>
            Aggiungi
          </button>
        </div>
      )}

      {/*  Il catalogo, che è l'altra metà della compilazione. Il modulo qui sopra fa una
           voce a mano — un corpo d'opera, una lavorazione — e va benissimo per quelle. Ma
           una riga senza codice articolo non ha un costo che venga da un listino, non ha
           un confronto fra fornitori e non si aggiorna quando arriva il listino nuovo: per
           il materiale si sceglie, non si scrive.

           Il capitolo di destinazione è scritto accanto, e non è un dettaglio: senza,
           l'articolo compare da qualche parte nell'elenco e chi l'ha messo deve cercarlo
           per capire dov'è finito.  */}
      {azioni && (
        <CercaArticolo
          dentro={capitoloDiCoda?.descrizione}
          inCorso={inCorso}
          onScelto={(a) =>
            azioni.aggiungiRiga({
              livello: 3,
              descrizione: a.descrizione,
              sottoRigaId: capitoloDiCoda?.id ?? null,
              articoloId: a.id,
            })
          }
        />
      )}
    </section>
      )}
    </FornitoreCarrello>
  );
}

/*  «INSERISCI ARTICOLO» IN FONDO A UN CAPITOLO.

    Chiesto da Galizzi il 15/10/2026, dopo una settimana di preventivi veri: chi si
    prepara prima tutti i capitoli e poi li riempie uno per volta si ritrovava ogni
    articolo nell'ultimo — «se quel giorno sono in velocita' e non mi accorgo che
    l'articolo mi finisce nel decimo, facciamo un pasticcio».

    Una riga sottile e chiusa, che si apre al clic: aperte tutte, dieci capitoli
    sarebbero dieci caselle di ricerca in mezzo alle voci. Dentro, il catalogo e la voce
    a mano, come in fondo alla tabella — e la riga nasce DOPO l'ultima del capitolo e
    COL CAPITOLO come padre, cosi' sta dove la si vede e conta dove deve.

    Il modulo in fondo resta: e' quello dei capitoli nuovi.  */
function InserisciNelCapitolo({
  capitolo,
  dopo,
  aperto,
  onAperto: setAperto,
  inCorso,
  azioni,
}: {
  capitolo: RigaPreventivoLetta;
  dopo: RigaPreventivoLetta;
  aperto: boolean;
  onAperto: (aperto: boolean) => void;
  inCorso: boolean;
  azioni: AzioniPreventivo;
}) {
  const [descrizione, setDescrizione] = useState('');

  const inserisci = (voce: { descrizione: string; articoloId?: string }) =>
    azioni.aggiungiRiga({
      livello: 3,
      descrizione: voce.descrizione,
      sottoRigaId: capitolo.id,
      dopoRigaId: dopo.id,
      articoloId: voce.articoloId ?? null,
    });

  return (
    <tr className="inserisci-nel-capitolo">
      <td colSpan={13}>
        {aperto ? (
          <div className="dentro-capitolo">
            <CercaArticolo
              dentro={capitolo.descrizione}
              inCorso={inCorso}
              onScelto={(a) => inserisci({ descrizione: a.descrizione, articoloId: a.id })}
            />
            <div className="a-mano">
              <input
                placeholder="Oppure una voce a mano"
                aria-label={`Voce a mano dentro ${capitolo.descrizione}`}
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && descrizione.trim() !== '') {
                    inserisci({ descrizione: descrizione.trim() });
                    setDescrizione('');
                  }
                }}
              />
              <button
                type="button"
                disabled={inCorso || descrizione.trim() === ''}
                onClick={() => {
                  inserisci({ descrizione: descrizione.trim() });
                  setDescrizione('');
                }}
              >
                Aggiungi
              </button>
              <button type="button" className="collegamento" onClick={() => setAperto(false)}>
                chiudi
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="collegamento" onClick={() => setAperto(true)}>
            + Inserisci articolo in «{capitolo.descrizione}»
          </button>
        )}
      </td>
    </tr>
  );
}

/*  Quante voci stanno sotto una testa, a qualunque profondità.

    Si scende invece di risalire perché la domanda è «quante ne sto chiudendo», e perché
    un capitolo che contiene un sottocapitolo che contiene degli articoli ne chiude tutte
    e due le file. Il visitato fa da cintura contro un ciclo nei riferimenti: non dovrebbe
    esistere, ma se esistesse questo giro bloccherebbe la schermata.  */
function discendenti(padreId: string, righe: RigaPreventivoLetta[]): number {
  const visitati = new Set<string>();
  const daGuardare = [padreId];

  while (daGuardare.length > 0) {
    const padre = daGuardare.pop()!;
    for (const riga of righe) {
      if (riga.rigaPadreId !== padre) continue;
      if (visitati.has(riga.id)) continue;
      visitati.add(riga.id);
      daGuardare.push(riga.id);
    }
  }

  return visitati.size;
}

/*  Una riga: si legge sempre, si corregge dove si può.

    I campi si modificano sul posto e il bottone di salvataggio compare solo quando
    qualcosa è cambiato — un «salva» sempre acceso invita a premerlo per sicurezza, e il
    salvataggio che non salva niente è il modo migliore per non fidarsi più di quello che
    si vede.  */
function RigaLetta({
  riga,
  sotto,
  prezzoDoppio = false,
  puoUscire = true,
  chiuso = false,
  onChiuso,
  inCorso,
  azioni,
}: {
  riga: RigaPreventivoLetta;
  sotto: number;
  /*  Il capitolo e' chiuso a schermo. `onChiuso` c'e' solo sui capitoli che hanno
      qualcosa da chiudere: una freccia su un capitolo vuoto non farebbe niente.  */
  chiuso?: boolean;
  onChiuso?: (chiuso: boolean) => void;
  /** C'e' un capitolo in cui uscire: altrimenti la freccia «esci» non si mostra. */
  puoUscire?: boolean;
  /*  Questo stesso articolo compare altrove nel preventivo a un prezzo diverso. Arriva
      gia' deciso da fuori: la domanda riguarda l'insieme delle righe, e una riga da sola
      non puo' rispondere.  */
  prezzoDoppio?: boolean;
  inCorso: boolean;
  azioni?: AzioniPreventivo;
}) {
  const [descrizione, setDescrizione] = useState(riga.descrizione);
  // La finestra del doppio clic sulla descrizione: vedi TestoEsteso.
  const [esteso, setEsteso] = useState(false);
  const [quantita, setQuantita] = useState(String(riga.quantita));
  const [unitaMisura, setUnitaMisura] = useState(riga.unitaMisura ?? '');
  const [sigla, setSigla] = useState(riga.siglaProduttore ?? '');
  const [codice, setCodice] = useState(riga.codiceArticoloProduttore ?? '');
  const unitaNote = useUnitaDiMisura();
  const [listino, setListino] = useState(
    riga.prezzoListinoUnitario === null ? '' : String(riga.prezzoListinoUnitario),
  );
  const [costo, setCosto] = useState(String(riga.costoUnitarioMateriale));
  const [ore, setOre] = useState(String(riga.oreUnitarie));
  //  Vuoto e' «a ore»: il campo vuoto e lo zero scritto sono due cose diverse, e lo
  //  zero e' una decisione — la posa compresa nel prezzo del materiale.
  const [posa, setPosa] = useState(riga.posaACorpo === null ? '' : String(riga.posaACorpo));
  const [ricarico, setRicarico] = useState(
    riga.ricaricoSpecificoPercentuale === null ? '' : String(riga.ricaricoSpecificoPercentuale),
  );
  //  Vuoto e' «nessuno mio»: la riga torna a ereditare dal capitolo. Uno zero scritto
  //  invece lo SPEGNE, ed e' la differenza fra «non ci ho pensato» e «ho deciso di no».
  const [sconto, setSconto] = useState(
    riga.scontoClientePercentuale === null ? '' : String(riga.scontoClientePercentuale),
  );

  /*  I CAMPI SI RIALLINEANO SOLO SE LA RIGA E' CAMBIATA DAVVERO.

      L'allineamento serve: quando la riga cambia sul server — la si salva, si rileggono
      i listini, la si sposta — le celle devono dire quello che dice il documento, non
      quello che c'era prima.

      Ma «e' arrivato un oggetto nuovo» non vuol dire «e' cambiata». Dopo OGNI
      salvataggio il preventivo si rilegge intero, quindi ogni riga arriva come oggetto
      nuovo anche se il suo contenuto non si e' mosso. Riallineare a quel colpo buttava
      via quello che era stato battuto nelle ALTRE righe: il 28/09/2026, scrivendo le
      quantita' su due righe e salvandone una, l'altra tornava a zero — e smetteva pure
      di essere marcata «da salvare», quindi senza nessun segnale che fosse successo.

      `ultimaLetta` tiene la versione su cui le celle sono allineate: finche' quella che
      arriva dice le stesse cose, non si tocca niente.  */
  const ultimaLetta = useRef(riga);

  useEffect(() => {
    if (stessiValoriCorreggibili(ultimaLetta.current, riga)) return;

    ultimaLetta.current = riga;
    setDescrizione(riga.descrizione);
    setQuantita(String(riga.quantita));
    setUnitaMisura(riga.unitaMisura ?? '');
    setSigla(riga.siglaProduttore ?? '');
    setCodice(riga.codiceArticoloProduttore ?? '');
    setListino(riga.prezzoListinoUnitario === null ? '' : String(riga.prezzoListinoUnitario));
    setCosto(String(riga.costoUnitarioMateriale));
    setOre(String(riga.oreUnitarie));
    setPosa(riga.posaACorpo === null ? '' : String(riga.posaACorpo));
    setRicarico(
      riga.ricaricoSpecificoPercentuale === null ? '' : String(riga.ricaricoSpecificoPercentuale),
    );
    setSconto(
      riga.scontoClientePercentuale === null ? '' : String(riga.scontoClientePercentuale),
    );
  }, [riga]);

  const articolo = riga.livello === 3;
  const correggibile = azioni !== undefined;

  const cambiata =
    descrizione !== riga.descrizione ||
    (numeroDecimale(quantita) ?? 0) !== riga.quantita ||
    unitaMisura !== (riga.unitaMisura ?? '') ||
    sigla !== (riga.siglaProduttore ?? '') ||
    codice !== (riga.codiceArticoloProduttore ?? '') ||
    (numeroDecimale(costo) ?? 0) !== riga.costoUnitarioMateriale ||
    numeroDecimale(listino) !== riga.prezzoListinoUnitario ||
    (numeroDecimale(ore) ?? 0) !== riga.oreUnitarie ||
    numeroDecimale(posa) !== riga.posaACorpo ||
    numeroDecimale(ricarico) !== riga.ricaricoSpecificoPercentuale ||
    numeroDecimale(sconto) !== riga.scontoClientePercentuale;

  /*  La scelta si salva da sola, senza passare dal «Salva la riga»: è una decisione
      sola, si prende con un gesto, e chiederne un secondo per confermarla è il modo di
      lasciarla a metà. Gli altri campi hanno il bottone perché si battono a mano e si
      sbagliano; questo no.

      Non viaggia dentro `salva()`: là si omette di proposito, così correggere una
      descrizione non riscrive anche come si stampa la distinta.  */
  function cambiaDistinta(distinta: string) {
    if (!azioni) return;
    azioni.modificaRiga(riga.id, {
      descrizione: riga.descrizione,
      quantita: riga.quantita,
      oreUnitarie: riga.oreUnitarie,
      posaACorpo: riga.posaACorpo,
      subappalto: riga.subappalto,
      codiceFornitoreMago: riga.codiceFornitoreMago,
      fornitore: riga.fornitore,
      costoUnitarioMateriale: riga.costoUnitarioMateriale,
      prezzoListinoUnitario: riga.prezzoListinoUnitario,
      unitaMisura: riga.unitaMisura,
      siglaProduttore: riga.siglaProduttore,
      codiceArticoloProduttore: riga.codiceArticoloProduttore,
      opzionale: riga.opzionale,
      visibileInStampa: riga.visibileInStampa,
      prezzoBloccato: riga.prezzoBloccato,
      ricaricoSpecificoPercentuale: riga.ricaricoSpecificoPercentuale,
      giaComprensivoDiMargine: riga.giaComprensivoDiMargine,
      distinta,
    });
  }

  /*  `testo` arriva dalla finestra del doppio clic, che salva da sola: in quel momento
      lo stato della descrizione non e' ancora cambiato, e il valore nuovo si passa qui.
      Gli altri campi corretti e non ancora salvati partono insieme — sono sulla stessa
      riga, e salvarne meta' lascerebbe l'altra meta' «da salvare» senza un perche'.  */
  function salva(testo?: string) {
    if (!azioni) return;
    const nuova = (testo ?? descrizione).trim();
    azioni.modificaRiga(riga.id, {
      descrizione: nuova === '' ? riga.descrizione : nuova,
      quantita: numeroDecimale(quantita) ?? 0,
      oreUnitarie: numeroDecimale(ore) ?? 0,
      //  Vuoto e' «a ore», zero e' «la posa non si paga»: due cose diverse, e confonderle
      //  rimetterebbe le ore per tariffa dove qualcuno aveva deciso di non farle pagare.
      posaACorpo: numeroDecimale(posa),
      subappalto: riga.subappalto,
      codiceFornitoreMago: riga.codiceFornitoreMago,
      fornitore: riga.fornitore,
      costoUnitarioMateriale: numeroDecimale(costo) ?? 0,
      // Vuoto vuol dire «non ce l'ha»: una voce a mano un listino non lo ha, e scriverci
      // zero direbbe che è gratis.
      prezzoListinoUnitario: numeroDecimale(listino),
      //  Vuoto vuol dire «non ce l'ha», non la stringa vuota: in stampa la colonna UM
      //  resta bianca, che e' quello che si vuole su una voce che non si misura.
      unitaMisura: unitaMisura.trim() === '' ? null : unitaMisura.trim(),
      //  Stessa regola: vuoto e' «non ce l'ha», non la stringa vuota. Una riga a mano
      //  senza codice non deve risultare «con un codice che e' il nulla».
      siglaProduttore: sigla.trim() === '' ? null : sigla.trim().toUpperCase(),
      codiceArticoloProduttore: codice.trim() === '' ? null : codice.trim(),
      opzionale: riga.opzionale,
      visibileInStampa: riga.visibileInStampa,
      prezzoBloccato: riga.prezzoBloccato,
      // Vuoto vuol dire «nessuno mio»: la riga torna a ereditare dal capitolo o dal generale.
      ricaricoSpecificoPercentuale: numeroDecimale(ricarico),
      // Stessa regola del ricarico: vuoto torna a ereditare, zero spegne.
      scontoClientePercentuale: numeroDecimale(sconto),
      giaComprensivoDiMargine: riga.giaComprensivoDiMargine,
    });
  }

  /*  La riga che si sta correggendo si marca, e non basta il bottone che compare.

      Le celle si scrivono ma non si salvano da sole — è una scelta, vedi sopra — e
      finché non si preme «Salva la riga» il prezzo in fondo alla riga resta quello
      di prima. Chi ha appena battuto una quantità vede quindi un numero fermo, e la
      conclusione naturale è che il programma non risponda, non che manchi un gesto.
      Il bottone da solo non lo dice: è piccolo, sta a sinistra, e l'occhio in quel
      momento è sulla colonna del prezzo, cioè dall'altra parte della riga.  */
  const daSalvare = correggibile && cambiata;

  /*  LA RIGA VUOTA esce di qui subito, con le sue tre mosse.

      Non e' una riga d'articolo con i campi spenti: non ha descrizione da correggere, non
      ha quantita' da battere e non ha un prezzo da guardare, quindi disegnarla con le
      celle vuote vorrebbe dire sei caselle in cui si puo' cliccare e in cui non succede
      niente. Quello che serve e' poterla spostare e poterla togliere.  */
  if (riga.separatore) {
    return (
      <tr className="riga-vuota">
        <CellaOrdine riga={riga} correggibile={correggibile} inCorso={inCorso} azioni={azioni} />
        <td colSpan={11} />
        {azioni && (
          <td className="destra comandi-riga">
            <button
              type="button"
              className="sposta"
              aria-label="Sposta la riga vuota più su"
              title="Più su, dentro il suo capitolo"
              disabled={inCorso}
              onClick={() => azioni.spostaRiga(riga.id, 'Su')}
            >
              <Icona nome="su" />
            </button>
            <button
              type="button"
              className="sposta"
              aria-label="Sposta la riga vuota più giù"
              title="Più giù, dentro il suo capitolo"
              disabled={inCorso}
              onClick={() => azioni.spostaRiga(riga.id, 'Giu')}
            >
              <Icona nome="giu" />
            </button>
            <button
              type="button"
              className="rimuovi"
              aria-label="Togli la riga vuota"
              disabled={inCorso}
              onClick={() => azioni.rimuoviRiga(riga.id)}
            >
              <Icona nome="rimuovi" />
            </button>
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr
      className={`livello-${riga.livello}${riga.opzionale ? ' fuori' : ''}${
        prezzoDoppio ? ' prezzo-doppio' : ''
      }${daSalvare ? ' da-salvare' : ''}`}
    >
      <CellaOrdine riga={riga} correggibile={correggibile} inCorso={inCorso} azioni={azioni} />
      <td>
        <div className={onChiuso ? 'testa-capitolo' : 'testa-voce'}>
        {onChiuso && (
          <button
            type="button"
            className="freccia-capitolo"
            aria-expanded={!chiuso}
            aria-label={`${chiuso ? 'Apri' : 'Chiudi'} il capitolo ${riga.descrizione}`}
            title={chiuso ? 'Apri il capitolo' : 'Chiudi il capitolo'}
            onClick={() => onChiuso(!chiuso)}
          >
            <Icona nome={chiuso ? 'capitolo-chiuso' : 'capitolo-aperto'} />
          </button>
        )}
        {correggibile ? (
          <input
            className="descrizione-riga"
            aria-label={`Descrizione di ${riga.descrizione}`}
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            onDoubleClick={() => setEsteso(true)}
            title="Doppio clic per leggere e scrivere la descrizione intera"
          />
        ) : (
          <span className="nome" onDoubleClick={() => setEsteso(true)}>
            {riga.descrizione}
          </span>
        )}
        {esteso && (
          <TestoEsteso
            titolo={riga.livello === 3 ? 'Descrizione della voce' : 'Descrizione del capitolo'}
            testo={correggibile ? descrizione : riga.descrizione}
            modificabile={correggibile && !inCorso}
            onChiudi={() => setEsteso(false)}
            onApplica={(testo) => {
              setDescrizione(testo);
              setEsteso(false);
              salva(testo);
            }}
          />
        )}
        <BottoneCarrello riga={riga} />
        </div>
        <span className="dettaglio elenco">
          {riga.siglaProduttore && (
            <em className="chiave">
              {riga.siglaProduttore} {riga.codiceArticoloProduttore}
            </em>
          )}
          {riga.opzionale && <em className="motivo">opzionale, fuori dal totale</em>}

          {/*  Come si presenta al cliente quello che sta sotto. Sta qui, sulla testa, e
               non su ogni accessorio: una spunta per riga andrebbe rimessa ogni volta che
               se ne aggiunge una, e la volta che qualcuno se ne dimentica il cliente
               riceve il prezzo di un raccordo da un euro in mezzo a voci da mille.  */}
          {!articolo && sotto > 0 && (
            <span className="distinta">
              {correggibile ? (
                <select
                  value={riga.distinta}
                  aria-label={`Come stampare la distinta di ${riga.descrizione}`}
                  disabled={inCorso}
                  onChange={(e) => cambiaDistinta(e.target.value)}
                >
                  <option value="Aperta">distinta aperta</option>
                  <option value="PrezziSullaTesta">prezzi solo qui</option>
                  <option value="Chiusa">solo questa riga</option>
                </select>
              ) : (
                riga.distinta !== 'Aperta' && (
                  <em>{riga.distinta === 'Chiusa' ? 'solo questa riga' : 'prezzi solo qui'}</em>
                )
              )}
              {riga.distinta !== 'Aperta' && (
                <em className="motivo">
                  {sotto === 1 ? 'una voce' : `${sotto} voci`}{' '}
                  {riga.distinta === 'Chiusa' ? 'fuori dalla stampa' : 'senza prezzo in stampa'}
                </em>
              )}
            </span>
          )}

          {/*  IL CODICE DEL PRODUTTORE, anche su una riga scritta a mano.

               «Posso inserire il codice in modo tale che poi in fase d'ordine ce l'ho
               gia' inserito» (Andrea, 01/10/2026, punto 3.2). Prima lo portava solo chi
               arrivava dal catalogo: una voce battuta a mano restava senza, e al momento
               di comprarla qualcuno doveva ricercarsela dal nome.

               SOTTO LA RIGA e non in colonna — «diventano troppe le cose messe in
               colonna» — come si fa gia' per il subappalto. Due caselle e non una,
               perche' sigla e codice sono due cose: «BRB» e' il produttore e si ripete su
               centinaia di righe, il codice no.

               NON SI VEDE IN STAMPA: «non lo facciamo vedere il codice dell'articolo al
               cliente, pero' rimane dentro a noi». Il modello attivo non lo scrive, e il
               segnaposto che lo porterebbe e' dichiarato come roba interna.  */}
          {articolo &&
            (correggibile ? (
              <span className="codice-produttore">
                cod.
                <input
                  className="sigla"
                  maxLength={8}
                  placeholder="sigla"
                  aria-label={`Sigla del produttore di ${riga.descrizione}`}
                  value={sigla}
                  onChange={(e) => setSigla(e.target.value)}
                />
                <input
                  maxLength={40}
                  placeholder="codice del produttore"
                  aria-label={`Codice del produttore di ${riga.descrizione}`}
                  value={codice}
                  onChange={(e) => setCodice(e.target.value)}
                />
              </span>
            ) : (
              riga.codiceArticoloProduttore !== null && (
                <span className="codice-produttore">
                  cod. {riga.siglaProduttore} {riga.codiceArticoloProduttore}
                </span>
              )
            ))}

          {prezzoDoppio && (
            <em className="motivo">stesso codice a un altro prezzo qui dentro</em>
          )}
          {/*  Il subappalto si legge sotto la voce, dove stanno le altre cose che
               spiegano la riga. Con il fornitore quando c'e': a preventivo capita spesso
               di sapere che si subappalta e non ancora a chi.  */}
          {riga.subappalto &&
            (correggibile && azioni !== undefined ? (
              <ChiLoFa riga={riga} inCorso={inCorso} azioni={azioni} />
            ) : (
              <span className="subappalto">
                subappalto{riga.fornitore ? ` · ${riga.fornitore}` : ''}
              </span>
            ))}
          {!riga.visibileInStampa && <span>fuori dalla stampa</span>}
          {riga.prezzoBloccato && <span>costo bloccato</span>}
          {riga.giaComprensivoDiMargine && <span>già comprensiva di margine</span>}
          {riga.ricaricoSpecificoPercentuale !== null && (
            <span>ricarico {formattaPercentuale(riga.ricaricoSpecificoPercentuale)}</span>
          )}
        </span>
      </td>

      {articolo ? (
        <>
          <td className="destra">
            {correggibile ? (
              /*  L'UNITA' DI MISURA ACCANTO ALLA QUANTITA', e non in una colonna sua.

                   La colonna c'era gia' in stampa e usciva vuota — «2» senza PZ, «260»
                   senza MT — perche' il campo stava sulla riga e non lo chiedeva
                   nessuno. Una decima colonna in una tabella che gia' scorre avrebbe
                   pagato due caratteri con altri sessanta pixel; accanto al numero e'
                   anche dove si legge, «260 MT».

                   Testo libero con i suggerimenti di Mago e non una tendina chiusa:
                   l'anagrafica di la' e' quella che e', e una riga scritta a mano deve
                   poter dire «gg» senza che qualcuno vada a crearla in amministrazione.  */
              <span className="quantita-e-unita">
                <input
                  className="numero-riga"
                  inputMode="decimal"
                  aria-label={`Quantità di ${riga.descrizione}`}
                  value={quantita}
                  onChange={(e) => setQuantita(e.target.value)}
                />
                <input
                  className="unita-riga"
                  list="unita-di-misura"
                  maxLength={8}
                  aria-label={`Unità di misura di ${riga.descrizione}`}
                  value={unitaMisura}
                  onChange={(e) => setUnitaMisura(e.target.value)}
                />
                <datalist id="unita-di-misura">
                  {unitaNote.map((u) => (
                    <option key={u.codice} value={u.codice}>
                      {u.descrizione}
                    </option>
                  ))}
                </datalist>
              </span>
            ) : (
              <>
                {formattaNumero(riga.quantita)} {riga.unitaMisura}
              </>
            )}
          </td>

          {/*  IL LISTINO LORDO. Si scrive a mano e non solo quando l'articolo arriva dal
               catalogo: meta' delle volte il listino sta su un'offerta del fornitore e non
               in un file importato, e senza il campo scrivibile la colonna resterebbe
               vuota proprio sulle righe in cui serve di piu'.

               Sotto, lo sconto che ne esce: e' un conto fatto dai due numeri accanto, non
               un terzo dato da tenere d'accordo con gli altri due.  */}
          <td className="destra listino">
            {correggibile ? (
              <input
                className="numero-riga"
                inputMode="decimal"
                aria-label={`Prezzo di listino di ${riga.descrizione}`}
                value={listino}
                onChange={(e) => setListino(e.target.value)}
              />
            ) : (
              riga.prezzoListinoUnitario !== null && formattaImporto(riga.prezzoListinoUnitario)
            )}
            {riga.scontoPercentuale !== null && (
              <em className="sconto">−{formattaPercentuale(riga.scontoPercentuale)}</em>
            )}
          </td>

          <td className="destra">
            {correggibile ? (
              <input
                className="numero-riga"
                inputMode="decimal"
                aria-label={`Prezzo d'acquisto di ${riga.descrizione}`}
                value={costo}
                onChange={(e) => setCosto(e.target.value)}
              />
            ) : (
              formattaImporto(riga.costoUnitarioMateriale)
            )}
          </td>

          {/*  Il totale d'acquisto non si scrive: e' quantita' per netto. Una cella
               scrivibile qui vorrebbe dire tre numeri di cui due si contraddicono.  */}
          <td className="destra calcolata">{formattaImporto(riga.costoMateriale)}</td>

          <td className="destra">
            {correggibile ? (
              <input
                className="numero-riga"
                inputMode="decimal"
                aria-label={`Ore unitarie di ${riga.descrizione}`}
                value={ore}
                onChange={(e) => setOre(e.target.value)}
              />
            ) : (
              formattaNumero(riga.oreUnitarie)
            )}
            {/*  Le ore in colonna sono quelle di UNA unita': e' quello che si batte. Il
                 totale compare accanto solo quando i due numeri non coincidono, che e'
                 l'unico caso in cui confonderli costa qualcosa.  */}
            {riga.ore !== riga.oreUnitarie && riga.ore > 0 && (
              <em className="sconto">{formattaNumero(riga.ore)} tot.</em>
            )}
          </td>

          {/*  LA POSA A CORPO. Vuoto vuol dire «a ore» ed e' il caso normale, quindi la
               colonna e' vuota quasi sempre: il segnaposto «a ore» dice cosa succede
               lasciandola stare, invece di far sembrare la cella un campo dimenticato.

               Le ore accanto restano scritte anche quando c'e' un importo, e non e' una
               svista: dicono quanti giorni si sta in cantiere, e da quelli escono viaggi,
               indennita' e pasti.  */}
          <td className="destra">
            {correggibile ? (
              <input
                className="numero-riga"
                inputMode="decimal"
                placeholder="a ore"
                aria-label={`Posa a corpo di ${riga.descrizione}`}
                title="Un importo fisso invece di ore per tariffa. Vuoto: si paga a ore."
                value={posa}
                onChange={(e) => setPosa(e.target.value)}
              />
            ) : riga.posaACorpo === null ? (
              ''
            ) : (
              formattaImporto(riga.posaACorpo)
            )}
          </td>
        </>
      ) : (
        /*  Un capitolo non ha quantità, listino né ore sue: quelle celle restano vuote
            invece di riempirsi di zeri, che lo farebbero sembrare una voce che non costa
            niente.  */
        <td className="destra" colSpan={6} />
      )}

      <Ricarico
        riga={riga}
        valore={ricarico}
        correggibile={correggibile}
        onCambia={setRicarico}
      />

      <Sconto
        riga={riga}
        valore={sconto}
        correggibile={correggibile}
        onCambia={setSconto}
      />

      {/*  I due numeri che vede il cliente: l'unitario con la manodopera dentro, e il
           totale della riga. L'unitario si calcola qui e non si salva — e' il totale
           diviso la quantita', e salvarlo vorrebbe dire un terzo numero da tenere
           d'accordo con gli altri due.  */}
      <td className="destra calcolata">
        {articolo && riga.quantita > 0
          ? formattaImporto(riga.prezzo / riga.quantita)
          : ''}
      </td>

      {/*  Su un articolo il suo prezzo; su un capitolo la somma di quello che gli sta
           sotto, in corsivo perché è un totale e non una voce.  */}
      <td className={`destra${riga.aggregato ? ' aggregato' : ''}`}>
        {riga.aggregato && riga.prezzo === 0 ? '' : formattaImporto(riga.prezzo)}
      </td>

      {azioni && (
        /*  I comandi della riga, dove la riga è, in due gruppi che rispondono a due
            domande diverse: «metti una riga» e «questa riga va da un'altra parte».

            Il primo gruppo — sopra, sotto, dentro — è per costruire. Il secondo — su,
            giù, esci, entra — è per correggere, ed è quello che mancava del tutto: finché
            l'unico modo di rimediare a una voce nata nel posto sbagliato era cancellarla e
            riscriverla, «dove va» era una decisione da prendere prima di scriverla. Un
            preventivo si compila per tentativi.

            La riga nuova nasce con una descrizione provvisoria perché il dominio non
            accetta una voce senza nome, e si corregge dove compare: il campo è già lì,
            in chiaro, e chiedere il testo in una finestrella prima di crearla sarebbe un
            passaggio in più su un'operazione che si ripete trenta volte.

            Nessun bottone si spegne da solo quando la mossa non si può fare. Sapere se una
            riga ha una sorella sopra, o un capitolo intorno, è roba del dominio: rifare
            quel giudizio qui vorrebbe dire due regole che col tempo divergono, e la
            schermata che spegne un comando che avrebbe funzionato. Chi preme una mossa
            impossibile riceve il motivo scritto — «è già la prima del suo capitolo» — che
            insegna la regola invece di lasciare indovinare perché il tasto è grigio.  */
        <td className="destra comandi-riga">
          <button
            type="button"
            aria-label={`Inserisci una riga sopra ${riga.descrizione}`}
            title="Inserisci una riga sopra"
            disabled={inCorso}
            onClick={() =>
              azioni.aggiungiRiga({
                livello: riga.livello,
                descrizione: 'Nuova riga',
                sottoRigaId: riga.rigaPadreId,
                primaRigaId: riga.id,
              })
            }
          >
            <Icona nome="inserisci-sopra" />
          </button>
          <button
            type="button"
            aria-label={`Inserisci una riga sotto ${riga.descrizione}`}
            title="Inserisci una riga sotto"
            disabled={inCorso}
            onClick={() =>
              azioni.aggiungiRiga({
                livello: riga.livello,
                descrizione: 'Nuova riga',
                sottoRigaId: riga.rigaPadreId,
                dopoRigaId: riga.id,
              })
            }
          >
            <Icona nome="inserisci-sotto" />
          </button>

          {/*  Lo stacco sotto questa riga. Sta qui e non solo nel modulo in fondo perche'
               «lascia aria dopo gli accessori» si decide guardando gli accessori: dal
               modulo in fondo la riga nascerebbe in coda e andrebbe risalita a mano
               trenta posizioni, che e' il modo di non usarlo mai.  */}
          <button
            type="button"
            aria-label={`Inserisci una riga vuota sotto ${riga.descrizione}`}
            title="Inserisci una riga vuota sotto"
            disabled={inCorso}
            onClick={() =>
              azioni.aggiungiRiga({
                livello: 3,
                descrizione: '',
                separatore: true,
                dopoRigaId: riga.id,
              })
            }
          >
            <Icona nome="riga-vuota" />
          </button>

          {/*  Dentro il capitolo, ed è il comando che mancava. Prima l'unico modo di
               mettere una voce dentro un titolo era il modulo in fondo, che aggancia
               all'ultimo capitolo aperto: funziona finché il capitolo è l'ultimo, e su un
               preventivo con cinque capitoli non funziona più.

               Nasce come articolo perché è quello che si mette dentro un capitolo il
               novantacinque per cento delle volte — la caldaia, il defangatore,
               l'installazione. Per un sottocapitolo si aggiunge un capitolo e lo si fa
               entrare con ⇥, che è la stessa mossa in due gesti invece di un secondo
               bottone su ogni riga.  */}
          {!articolo && (
            <button
              type="button"
              className="dentro"
              aria-label={`Aggiungi una voce dentro ${riga.descrizione}`}
              title="Aggiungi una voce dentro questo capitolo"
              disabled={inCorso}
              onClick={() =>
                azioni.aggiungiRiga({
                  livello: 3,
                  descrizione: 'Nuova voce',
                  sottoRigaId: riga.id,
                })
              }
            >
              <Icona nome="aggiungi-dentro" />
            </button>
          )}

          <button
            type="button"
            className="sposta"
            aria-label={`Sposta ${riga.descrizione} più su`}
            title="Più su, dentro il suo capitolo"
            disabled={inCorso}
            onClick={() => azioni.spostaRiga(riga.id, 'Su')}
          >
            <Icona nome="su" />
          </button>
          <button
            type="button"
            className="sposta"
            aria-label={`Sposta ${riga.descrizione} più giù`}
            title="Più giù, dentro il suo capitolo"
            disabled={inCorso}
            onClick={() => azioni.spostaRiga(riga.id, 'Giu')}
          >
            <Icona nome="giu" />
          </button>
          {puoUscire && (
            <button
              type="button"
              className="sposta"
              aria-label={`Fai uscire ${riga.descrizione} dal suo capitolo`}
              title="Falla uscire dal capitolo"
              disabled={inCorso}
              onClick={() => azioni.spostaRiga(riga.id, 'Fuori')}
            >
              <Icona nome="esci" />
            </button>
          )}
          <button
            type="button"
            className="sposta"
            aria-label={`Fai entrare ${riga.descrizione} nella riga sopra`}
            title="Falla entrare nella riga sopra"
            disabled={inCorso}
            onClick={() => azioni.spostaRiga(riga.id, 'Dentro')}
          >
            <Icona nome="entra" />
          </button>
          {/*  LE DUE SPUNTE DEL SUBAPPALTO, solo sugli articoli.

               La prima dice che questa voce la fa un altro: non sposta un euro, sposta il
               costo dalla fetta dei materiali a quella dei subappalti, ed e' l'unica cosa
               che permette di leggere un margine su un lavoro dove meta' del valore lo fa
               un'impresa esterna.

               La seconda decide se il cliente la vede. E' la scelta di Andrea del
               19/09: non una regola fissa per tutti i subappalti — «mai in offerta»
               sarebbe comoda e sbagliata, capita di doverlo esporre — ma una spunta sulla
               riga, e la spunta c'era gia': `VisibileInStampa`, che finora nessuna
               schermata accendeva.  */}
          {articolo && (
            <button
              type="button"
              className={riga.subappalto ? 'bloccato' : ''}
              aria-label={
                riga.subappalto
                  ? `${riga.descrizione} non e' piu' un subappalto`
                  : `Segna ${riga.descrizione} come subappalto`
              }
              title={
                riga.subappalto
                  ? 'Subappalto: il costo sta nella sua voce, non fra i materiali'
                  : 'Questa voce la fa un altro'
              }
              disabled={inCorso}
              onClick={() =>
                azioni.modificaRiga(riga.id, {
                  descrizione: riga.descrizione,
                  quantita: riga.quantita,
                  oreUnitarie: riga.oreUnitarie,
                  posaACorpo: riga.posaACorpo,
                  subappalto: !riga.subappalto,
                  codiceFornitoreMago: riga.codiceFornitoreMago,
                  fornitore: riga.fornitore,
                  costoUnitarioMateriale: riga.costoUnitarioMateriale,
                  prezzoListinoUnitario: riga.prezzoListinoUnitario,
                  unitaMisura: riga.unitaMisura,
                  siglaProduttore: riga.siglaProduttore,
                  codiceArticoloProduttore: riga.codiceArticoloProduttore,
                  opzionale: riga.opzionale,
                  visibileInStampa: riga.visibileInStampa,
                  prezzoBloccato: riga.prezzoBloccato,
                  ricaricoSpecificoPercentuale: riga.ricaricoSpecificoPercentuale,
                  giaComprensivoDiMargine: riga.giaComprensivoDiMargine,
                })
              }
            >
              <Icona nome="subappalto" />
            </button>
          )}

          {articolo && (
            <button
              type="button"
              className={riga.visibileInStampa ? '' : 'bloccato'}
              aria-label={
                riga.visibileInStampa
                  ? `Togli ${riga.descrizione} dalla stampa`
                  : `Rimetti ${riga.descrizione} nella stampa`
              }
              title={
                riga.visibileInStampa
                  ? 'Il cliente la legge in offerta'
                  : 'Fuori dalla stampa: resta nei conti, sparisce dall\'offerta'
              }
              disabled={inCorso}
              onClick={() =>
                azioni.modificaRiga(riga.id, {
                  descrizione: riga.descrizione,
                  quantita: riga.quantita,
                  oreUnitarie: riga.oreUnitarie,
                  posaACorpo: riga.posaACorpo,
                  subappalto: riga.subappalto,
                  codiceFornitoreMago: riga.codiceFornitoreMago,
                  fornitore: riga.fornitore,
                  costoUnitarioMateriale: riga.costoUnitarioMateriale,
                  prezzoListinoUnitario: riga.prezzoListinoUnitario,
                  unitaMisura: riga.unitaMisura,
                  siglaProduttore: riga.siglaProduttore,
                  codiceArticoloProduttore: riga.codiceArticoloProduttore,
                  opzionale: riga.opzionale,
                  visibileInStampa: !riga.visibileInStampa,
                  prezzoBloccato: riga.prezzoBloccato,
                  ricaricoSpecificoPercentuale: riga.ricaricoSpecificoPercentuale,
                  giaComprensivoDiMargine: riga.giaComprensivoDiMargine,
                })
              }
            >
              <Icona nome={riga.visibileInStampa ? 'visibile' : 'nascosto'} />
            </button>
          )}

          {/*  Il lucchetto: un prezzo concordato col fornitore non deve muoversi quando
               arriva un listino nuovo. Sta qui e non dentro una scheda perché è una
               decisione che si prende guardando la riga, e perché senza un modo di
               metterlo lo sblocco che rilegge i listini non servirebbe a nessuno.  */}
          {articolo && (
            <button
              type="button"
              className={riga.prezzoBloccato ? 'bloccato' : ''}
              aria-label={
                riga.prezzoBloccato
                  ? `Sblocca il costo di ${riga.descrizione}`
                  : `Blocca il costo di ${riga.descrizione}`
              }
              /*  «COSTO» e non «prezzo», dal 07/10/2026. Il lucchetto tiene fuori i
                  listini dal costo della riga, e il prezzo da quel costo si ricalcola
                  lo stesso: cambiando il ricarico si muove. Chiamarlo «prezzo» ha
                  fatto aspettare a Galizzi un prezzo fermo — «invece ha cambiato anche
                  quella li'», 01/10 — e il nome era l'unica cosa che glielo prometteva.  */
              title={
                riga.prezzoBloccato
                  ? 'Costo bloccato: i listini non lo toccano. Il prezzo si ricalcola lo stesso se cambia il ricarico'
                  : 'Blocca il costo contro gli aggiornamenti di listino'
              }
              disabled={inCorso}
              onClick={() =>
                azioni.modificaRiga(riga.id, {
                  descrizione: riga.descrizione,
                  quantita: riga.quantita,
                  oreUnitarie: riga.oreUnitarie,
                  posaACorpo: riga.posaACorpo,
                  subappalto: riga.subappalto,
                  codiceFornitoreMago: riga.codiceFornitoreMago,
                  fornitore: riga.fornitore,
                  costoUnitarioMateriale: riga.costoUnitarioMateriale,
                  prezzoListinoUnitario: riga.prezzoListinoUnitario,
                  unitaMisura: riga.unitaMisura,
                  siglaProduttore: riga.siglaProduttore,
                  codiceArticoloProduttore: riga.codiceArticoloProduttore,
                  opzionale: riga.opzionale,
                  visibileInStampa: riga.visibileInStampa,
                  prezzoBloccato: !riga.prezzoBloccato,
                  ricaricoSpecificoPercentuale: riga.ricaricoSpecificoPercentuale,
                  giaComprensivoDiMargine: riga.giaComprensivoDiMargine,
                })
              }
            >
              <Icona nome={riga.prezzoBloccato ? 'bloccato' : 'sbloccato'} />
            </button>
          )}
          <button
            type="button"
            aria-label={
              articolo ? `Copia ${riga.descrizione}` : `Copia ${riga.descrizione} con il suo contenuto`
            }
            title={articolo ? 'Copia la riga' : 'Copia il capitolo con tutto quello che ci sta sotto'}
            disabled={inCorso}
            onClick={() => azioni.duplicaRiga(riga.id)}
          >
            <Icona nome="copia" />
          </button>
          <button
            type="button"
            className="rimuovi"
            aria-label={`Togli ${riga.descrizione}`}
            disabled={inCorso}
            onClick={() => azioni.rimuoviRiga(riga.id)}
          >
            <Icona nome="rimuovi" />
          </button>

          {/*  «SALVA LA RIGA» IN FONDO A DESTRA, sotto l'ultima colonna (Andrea,
               01/10/2026, punto 3.4). Stava in fondo alla descrizione, cioe' all'altro
               capo di una tabella che scorre: si batteva una quantita' a sinistra,
               l'occhio andava sul totale a destra — che resta fermo, perche' il prezzo lo
               rifa' il server — e il bottone da premere era indietro, fuori dallo sguardo.

               Qui sta dove la riga finisce e dove stanno gia' i suoi comandi, a un palmo
               dal numero che si e' appena guardato. Da quando i «Salva» intermedi non ci
               sono piu' e' anche l'unico rimasto nella scheda, e un bottone solo conviene
               tenerlo dove lo si cerca.

               Occupa tutta la larghezza della griglia dei comandi: e' una frase, non
               un'icona, e in 25 px si leggerebbe «Salv…».  */}
          {cambiata && (
            <button type="button" className="salva-riga" disabled={inCorso} onClick={() => salva()}>
              Salva la riga
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

/*  IL NUMERO D'ORDINE della riga, scrivibile.

    Si batte un numero e si preme Invio: la riga ci va, portandosi dietro quello che ha
    sotto. Invio e non un bottone «salva» perche' e' una mossa sola e reversibile — si
    riscrive il numero di prima e si torna indietro — mentre i campi accanto, che si
    battono a mano e si sbagliano, il bottone ce l'hanno apposta.

    Il numero si mostra senza decimali quando ne ha (11,25 diventa 11,25, ma 10,0000
    diventa 10): i decimali nascono dagli inserimenti in mezzo e non li scrive nessuno,
    vederli farebbe solo rumore.  */
function CellaOrdine({
  riga,
  correggibile,
  inCorso,
  azioni,
}: {
  riga: RigaPreventivoLetta;
  correggibile: boolean;
  inCorso: boolean;
  azioni?: AzioniPreventivo;
}) {
  const mostrato = String(Number(riga.posizione));
  const [scritto, setScritto] = useState(mostrato);

  useEffect(() => setScritto(String(Number(riga.posizione))), [riga.posizione]);

  if (!correggibile || azioni === undefined)
    return <td className="destra ordine">{mostrato}</td>;

  const manda = () => {
    const codice = numeroDecimale(scritto);
    if (codice === null || codice <= 0 || codice === Number(riga.posizione)) {
      setScritto(mostrato);
      return;
    }
    azioni.spostaRigaAlCodice(riga.id, codice);
  };

  /*  Il numero battuto e non ancora mandato. Si colora come le altre celle in sospeso
      della riga — stesso ambra di «da salvare» — perche' e' la stessa cosa detta a chi
      guarda: qui c'e' qualcosa che non e' ancora successo. Senza, il campo con dentro
      un numero nuovo e' identico a uno con dentro il numero di prima.  */
  const inSospeso = scritto !== mostrato;

  return (
    <td className="destra ordine">
      <input
        className={`numero-riga${inSospeso ? ' in-sospeso' : ''}`}
        inputMode="decimal"
        aria-label={`Numero d'ordine di ${riga.separatore ? 'la riga vuota' : riga.descrizione}`}
        title="Scrivi il numero e premi Invio: la riga si sposta li'. Esc annulla."
        disabled={inCorso}
        value={scritto}
        onChange={(e) => setScritto(e.target.value)}
        onBlur={manda}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setScritto(mostrato);
        }}
      />
    </td>
  );
}

/*  Lo sconto al cliente della riga, col colore che dice da dove viene.

    Gemello di `Ricarico` qui sotto, e di proposito: la precedenza e' la stessa — riga,
    poi capitolo, poi niente — e chi lavora ha una regola sola da ricordare.

    Il colore dice una cosa sola: c'e' uno sconto o non c'e'. Il verde del ricarico
    significa «qualcuno ha deciso», e qui vale lo stesso; il rosso dello zero no — uno
    sconto a zero e' il caso normale, non un errore, e colorarlo di rosso insegnerebbe a
    ignorare i rossi.

    Evidenziato, come chiesto in riunione: «lo sconto l'ha scritto cosi', forse lo
    scriverei piu' evidenziato».  */
function Sconto({
  riga,
  valore,
  correggibile,
  onCambia,
}: {
  riga: RigaPreventivoLetta;
  valore: string;
  correggibile: boolean;
  onCambia: (valore: string) => void;
}) {
  const scontata = (riga.scontoApplicato ?? 0) > 0;

  const spiegazione =
    riga.fonteSconto === 'Riga'
      ? 'Sconto scritto su questa riga'
      : riga.fonteSconto === 'Capitolo'
        ? 'Ereditato dal capitolo che la contiene'
        : 'Nessuno sconto su questa riga';

  return (
    <td className={`destra sconto-cliente ${scontata ? 'attivo' : ''}`} title={spiegazione}>
      {correggibile ? (
        <input
          className="numero-riga"
          inputMode="decimal"
          aria-label={`Sconto al cliente di ${riga.descrizione}`}
          /*  Il segnaposto mostra quello ereditato dal capitolo: cosi' su una riga
              vuota si legge lo sconto che sta comunque subendo, invece di una casella
              vuota che sembra dire «nessuno sconto».  */
          placeholder={riga.fonteSconto === 'Capitolo' ? String(riga.scontoApplicato) : ''}
          value={valore}
          onChange={(e) => onCambia(e.target.value)}
        />
      ) : (
        formattaPercentuale(riga.scontoApplicato)
      )}
    </td>
  );
}

/*  Il ricarico della riga, col colore che dice da dove viene.

    È la codifica chiesta in riunione, e serve a leggere trenta righe in un colpo d'occhio:
    trasparente è il ricarico generale, cioè quello che la procedura ha messo su tutto;
    verde è una forzatura — sulla riga o ereditata dal capitolo — cioè un punto in cui
    qualcuno ha deciso qualcosa; rosso è lo zero, la riga che non guadagna niente e che
    quasi sempre è un errore. «Guarda che lì non hai l'aumento su quelle voci».

    Il campo scrive il ricarico DELLA RIGA: lasciarlo vuoto la fa tornare a ereditare, e
    su un capitolo scriverlo lo dà a tutto quello che ci sta sotto.  */
function Ricarico({
  riga,
  valore,
  correggibile,
  onCambia,
}: {
  riga: RigaPreventivoLetta;
  valore: string;
  correggibile: boolean;
  onCambia: (valore: string) => void;
}) {
  if (riga.fonteRicarico === 'GiaNelCosto') {
    return (
      <td className="destra ricarico gia-nel-costo" title="Già comprensiva di margine: non si ricarica">
        —
      </td>
    );
  }

  const azzerato = riga.ricaricoApplicato === 0;
  const forzato = riga.fonteRicarico === 'Riga' || riga.fonteRicarico === 'Capitolo';
  const classe = azzerato ? 'azzerato' : forzato ? 'forzato' : '';

  const spiegazione =
    riga.fonteRicarico === 'Riga'
      ? 'Ricarico scritto su questa riga'
      : riga.fonteRicarico === 'Capitolo'
        ? 'Ereditato dal capitolo che la contiene'
        : 'Il ricarico generale del preventivo';

  return (
    <td className={`destra ricarico ${classe}`} title={spiegazione}>
      {correggibile ? (
        <input
          className="numero-riga"
          inputMode="decimal"
          aria-label={`Ricarico di ${riga.descrizione}`}
          placeholder={riga.ricaricoApplicato === null ? '' : String(riga.ricaricoApplicato)}
          value={valore}
          onChange={(e) => onCambia(e.target.value)}
        />
      ) : (
        formattaPercentuale(riga.ricaricoApplicato)
      )}
    </td>
  );
}
